import { cloneElement, isValidElement, lazy, Suspense, useCallback, useEffect, useMemo, useRef,
         useState } from "react";
import { seAsteapta } from "../pages/generare";
import type { StareGenerare } from "../pages/generare";
import type { PointerEvent } from "react";
import * as api from "../api";
import ScheletIncarcare from "../structure/ScheletIncarcare";
import type { Silueta } from "../api";
import type { SolutieRand } from "../nav/api";
import { useRegistruOptional } from "../nav/Registru";
import { adnotareInCadru, fantomeDePus, inventarDocument, isGeoreferenced,
         laOrigineLocala, northingFirst, northUp, numeNivel, origineDesen, storeysOf,
         toApartmentLevelForStorey, toPlans, toSiteLayer, toStoreyPlans, regimulArhitecturii,
         type CanonicalDoc, type StoreyPlan } from "./canonical";
import type { Column } from "./derive";
import { Defs } from "./Defs";
import Inspector from "./Inspector";
import RecompPanou, { type VariantaPePlan } from "./RecompPanou";
import { arata as arataToast } from "../toasts";
import { selectionOfId, ringOfSelection, type VAnno, type VRef,
         type VStatus } from "./validation";
import { describeSelection, planulSelectiei, sameSelection, selectionFromTarget,
         type Selection } from "./inspect";
import ApartmentView from "./plan/ApartmentView";
import AnnotationLayer from "./plan/AnnotationLayer";
import { FilmLayer } from "./plan/FilmLayer";
import RiglaLayer, { type Masura } from "./plan/RiglaLayer";
import { type CadruFilm } from "./plan/film";
import { punctInCadru } from "./canonical";
import { PlanView } from "./plan/PlanView";
import SitePlanView from "./plan/SitePlanView";
import { A01, CompassRose } from "./plan/siteGlyphs";
import IndicatoriCard from "./IndicatoriCard";
import StoreyView, { PALETA,
                     type Conventie, type Strat } from "./plan/StoreyView";
import Fantome from "./plan/Fantome";
import SchitaFantoma from "./plan/SchitaFantoma";
import EvacuareTrasee, { traseulLocului, type TraseuEvacuare } from "./plan/EvacuareTrasee";
import SemneCirculatie, { type SemnCirculatie } from "./plan/SemneCirculatie";
import { cifreArhitectura, registrulDocumentului, type CifreGrup } from "./plan/arhLegenda";
import LegendaFoc from "./plan/LegendaFoc";
import AdnotariNivel from "./plan/AdnotariNivel";
import { docCuVariante } from "./plan/recompPreviz";
import { Icoana, type NumeIcoana } from "./tronson/icoane";
import { Ic } from "../components/ui";
import { scena3D } from "./plan/arh3d";
import type { Vedere3 } from "./plan/Randare3D";
import { scenaSit } from "./plan/sit3d";
import { studiuInsorire } from "./plan/insorire";
import PanouFatada, { type MachetaFatadei } from "./fatada/PanouFatada";
import AsistentPlan from "./plan_agent/AsistentPlan";
import { golesteTot as golesteAnularileFatadei } from "./fatada/stilSolutie";
import { useRolProiect } from "../components/rol";
import { potPleca } from "../nescris";
import {
  acumLa, cardinal, decalajFus, efemeride, locSit, pozitiaSoarelui,
} from "./plan/soare";

/** Macheta trage three.js după ea (~600 KB). Adusă static, ar intra în bundle-ul
 *  fiecărui ecran al platformei — pentru o vedere pe care mulți n-o deschid
 *  niciodată. `lazy` o aduce la prima apăsare pe «2D» sau «3D». */
const Randare3D = lazy(() => import("./plan/Randare3D"));
// MASA EDITORULUI (vederea «Plan» a unei soluții arhitecturale) aduce după ea
// paleta, panoul și modelul editorului de tronsoane — nu intră în bundle-ul
// vizualizatorului până nu se deschide chiar un plan de editat.
const EditorNivel = lazy(() => import("./plan/EditorNivel"));
import { useEditorParcaj } from "./parcaj/useEditorParcaj";
/** Macheta de situație are propriul ei three.js de adus — și e o vedere pe care
 *  o deschid și mai puțini, fiindcă e a unei singure discipline. */
const RandareSit3D = lazy(() => import("./plan/RandareSit3D"));
import type { SolvedPlan } from "./client";
import type { Pt } from "./types";
import type { ZonaMacheta } from "./plan/selectie3d";
import { coloaneleFetelor } from "./fatada/selectie";
import DetaliiElement from "./fatada/DetaliiElement";
import SelectorEtaj from "./plan/SelectorEtaj";
import { etajeleMachetei } from "./plan/interiorEtaj";
import { dimensiuniCote } from "./fatada/detalii";
import { numar, useT, useTn } from "../i18n";
import type { Cheie } from "../i18n";
import { etapaJobului } from "../solutions/shared";
import { type Bbox, type View } from "./view";
import { useDocumentSolutie } from "./useDocumentSolutie";
import { useArhitecturaInlantuita } from "./useArhitecturaInlantuita";
import StratOras2D from "./oras/StratOras2D";
import { useOrasInCadru } from "./oras/useOrasInCadru";
import { useStratOras } from "./oras/useStratOras";
import { useComenziPanZoom, usePanZoom, useScaraDetaliului } from "./usePanZoom";

// Vizualizatorul adus din aplicația de arhitectură, legat la platformă.
//
// Componentele de desen (`PlanView`, `ApartmentView`, `SitePlanView`) și
// traducerea documentului în modele de desen (`canonical.ts`) sunt luate CA
// ATARE: sunt ce știe deja să deseneze locuri de parcare, apartamente, nuclee
// și alei, cu tot cu selecția pe click. Ce s-a schimbat e de unde vine
// documentul — de la control plane, nu de la API-ul aplicației vechi.
//
// Ce NU s-a adus: editorul (desenarea lotului, pornirea unui solve, exportul
// PDF, adnotările). Aici se VEDE o soluție deja generată.

/** Un obiect din document, cât îl citește ecranul ăsta. */
type Obiect = {
  id: string;
  type: string;
  properties?: { level?: number | null; building?: string | null;
                 area?: string | null } | null;
  geometry?: { poly?: number[][] | null } | null;
};

/** Cine deține un nivel: clădirea, sau — la subsol — excavația. */
const proprietarul = (nivel: Obiect) =>
  nivel.properties?.building ?? nivel.properties?.area ?? undefined;

/** CORPUL ca opțiune de selector: id-ul obiectului care deține planșeele. */
export const TOATE_CORPURILE = "*";

/** Cele două întrebări ale unui plan de arhitectură, ținute SEPARAT: la ce cotă
 *  te uiți, și la care corp.
 *
 *  Erau una singură, iar lista arăta «Parter · C1», «Parter · C2», «Etaj 1 · C1»
 *  … — produsul cartezian, într-un singur rând de butoane. Pe un ansamblu cu
 *  patru corpuri și P+3 asta face douăzeci de butoane pentru două întrebări de
 *  patru și cinci răspunsuri, iar „arată-mi tot parterul" nu se putea cere
 *  deloc: nicio combinație nu însemna «toate corpurile».
 *
 *  Separate, selectorul de corp poate avea «Toate corpurile» — care e chiar
 *  planșa de etaj a ansamblului, desenul pe care îl scoate un birou — iar cel de
 *  nivel rămâne o scară de cote, în ordinea în care se citește un plan: de jos
 *  în sus.
 */

export type Nivel = { level: number; eticheta: string };
export type Corp = { id: string; eticheta: string };

function niveluriDistincte(niveluri: StoreyPlan[]): Nivel[] {
  const cote = new Map<number, StoreyPlan[]>();
  for (const n of niveluri) {
    const list = cote.get(n.level) ?? [];
    list.push(n);
    cote.set(n.level, list);
  }
  return [...cote.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([level, pe]) => ({
      level,
      // «Parcare · nivel −1» doar dacă TOT ce stă la cota asta e parcaj. Cu un
      // singur corp de parcaj din patru, numele cotei ar fi mințit despre
      // celelalte trei.
      eticheta: numeNivel(level, pe.every((n) => n.parcare)),
    }));
}

/** Corpurile care ajung la cota asta. Nu toate corpurile documentului: un P+8
 *  n-are ce căuta în selectorul etajului 10, iar un buton care duce la o pânză
 *  goală e mai rău decât un buton lipsă. */
function corpuriLaCota(niveluri: StoreyPlan[], level: number): Corp[] {
  const out = new Map<string, string>();
  for (const n of niveluri) {
    if (n.level !== level || !n.owner) continue;
    // Eticheta e codul corpului («C1»), luat din `label` — `toStoreyPlans` îl
    // adaugă acolo doar când mai multe corpuri ating cota. Când nu, e un singur
    // corp și numele lui nu deosebește nimic, deci se cere din document.
    out.set(n.owner, n.cod || n.owner.slice(0, 8));
  }
  return [...out.entries()].sort((a, b) => a[1].localeCompare(b[1], "ro"))
    .map(([id, eticheta]) => ({ id, eticheta }));
}

/** Cota la care se deschide un document: parterul, când există.
 *
 *  Fără parter (un document cu numai subsoluri, sau cu etajele unui tronson
 *  care începe mai sus), cel mai de jos nivel DE DEASUPRA solului; iar dacă
 *  nici ăla nu e, cea mai de sus cotă — la un subsol pur, ultimul subsol e cel
 *  mai aproape de parter. */
export function nivelImplicit(cote: readonly Nivel[]): number | null {
  if (!cote.length) return null;
  const parter = cote.find((c) => c.level === 0);
  if (parter) return parter.level;
  const peste = cote.filter((c) => c.level > 0).sort((a, b) => a.level - b.level);
  if (peste.length) return peste[0].level;
  return cote[cote.length - 1].level;
}

/** Rampa internă, DOAR dacă are coordonate.
 *
 *  Documentul poate veni cu un obiect `Ramp` scris de altă etapă — rampa de
 *  acces din stradă a planului de situație, care n-are `cx`/`cy`/`run_m`.
 *  Trimisă mai departe, ajunge în desen ca `translate(undefined undefined)`:
 *  nu o eroare, o formă care nu se vede și un atribut invalid în SVG. */
function rampaValida(plan: SolvedPlan) {
  const r = plan.rampInt;
  return r && Number.isFinite(r.cx) && Number.isFinite(r.cy) ? r : null;
}

/** Un element cerut din afara viewer-ului: din URL, sau de pe ecranul de
 *  Verificări. Poartă ID-ul documentului, nu indici — vezi `selectionOfId`:
 *  indicii sunt unde ERA elementul când s-a scris raportul, iar ID-ul e unde e
 *  acum. */
export interface Tinta { kind: string; level: number; id: string }

/** `kind|level|id`, pentru URL. Bara verticală nu apare în niciunul din cele
 *  trei, deci nu trebuie scăpată — iar un link rămâne citibil de om. */
export function tintaToParam(t: Tinta): string {
  return `${t.kind}|${t.level}|${t.id}`;
}

export function tintaFromParam(v: string | null): Tinta | null {
  if (!v) return null;
  const [kind, level, ...rest] = v.split("|");
  const id = rest.join("|");
  const n = Number(level);
  return kind && id && Number.isFinite(n) ? { kind, level: n, id } : null;
}


/** Familia unui nivel pentru implicitele stratului ISU: parcajul (subsolul) și
 *  restul. `parcare`, ca peste tot în planșă — v. `StoreyView.eDescarcare`. */
type Familie = "subsol" | "etaje";
const familia = (n: StoreyPlan): Familie => (n.parcare ? "subsol" : "etaje");

export default function Vizualizator({ projectId, solution, plin = false,
                                       compact = false, tinta = null,
                                       editabil = false,
                                       adnotare = null, nivelInitial = null,
                                       cadruFilm = null, silueta = null,
                                       depanare = false,
                                       stareMacheta, blocSolutie = null }:
                                     { projectId: string; solution: SolutieRand;
                                       /** Cota pe care se deschide pânza, când
                                        *  documentul o are — ecranul de încercări
                                        *  vrea parcarea (-1), nu parterul. */
                                       nivelInitial?: number | null;
                                       /** Canvasul umple tot ecranul, iar selectorul
                                        *  și inspectorul plutesc peste el — felul în
                                        *  care e desenat Studio în machetă. */
                                       plin?: boolean;
                                       /** Vederea «Plan» a unei soluții
                                        *  arhitecturale se lucrează pe masa
                                        *  editorului (v. `EditorNivel`). Numai
                                        *  Studioul o cere: încercările și
                                        *  verificările doar se uită. */
                                       editabil?: boolean;
                                       /** Doar pânza: fără cap, fără selector, fără
                                        *  inspector. Pentru o previzualizare de
                                        *  câteva sute de pixeli, unde tot ce e în
                                        *  jurul desenului mănâncă desenul. Aceleași
                                        *  `PlanView`/`ApartmentView` desenează. */
                                       compact?: boolean;
                                       /** Elementul de selectat la încărcare. */
                                       tinta?: Tinta | null;
                                       /** Un cadru din filmul de împachetare, desenat
                                        *  peste planșă: rețeaua de alei după pasul ales,
                                        *  cu ce a adăugat și ce a scos pasul. Ecranul de
                                        *  încercări îl alege; restul viewer-ului nu știe
                                        *  de el. Cât e aprins, planșa de parcaj FINITĂ
                                        *  nu-și mai desenează locurile și aleile — v.
                                        *  `filmAprins`. */
                                       cadruFilm?: CadruFilm | null;
                                       /** Uneltele de DEPANARE VIZUALĂ: rigla
                                        *  (două clicuri pe desen și scrie cât e
                                        *  între ele) și comutatorul rampelor.
                                        *  Numai ecranul de încercări le cere
                                        *  (Oana, 25.09.2026): pe Studio și pe
                                        *  restul viewer-ului meniul rămâne
                                        *  curat, cu cele trei filtre. */
                                       depanare?: boolean;
                                       /** Desenul validatorului: cota măsurată,
                                        *  traseul parcurs, tronsonul care se
                                        *  strânge. Fără el, o selecție arată CE
                                        *  element a picat, dar nu de ce. */
                                       adnotare?: { anno: VAnno; status: VStatus } | null;
                                       /** Silueta clădirii, pentru scheletul care
                                        *  ține locul desenului cât se încarcă —
                                        *  v. `structure/ScheletIncarcare`. */
                                       silueta?: Silueta | null;
                                       /** Pe planul de situație: arhitectura
                                        *  din care se compune macheta, gata,
                                        *  în lucru sau pe drum. Cât nu e gata
                                        *  dar vine, 3D-ul stă stins, cu firul
                                        *  de sub etichetă — v.
                                        *  `pages/generare.ts`. */
                                       stareMacheta?: StareGenerare;
                                       /** Soluția, în capul meniului plutitor:
                                        *  numele, fazele, acțiunile și
                                        *  «Schimbă ▾» (v. `nav/BlocSolutie`).
                                        *  Numai în vizualizatorul plin. */
                                       blocSolutie?: React.ReactNode }) {
  const [doc, setDoc] = useState<CanonicalDoc | null>(null);
  /** CÂT din document e pe ecran. `complet: false` = a venit felia de start —
   *  contextul plus o cotă — iar restul e pe drum. Ce depinde de tot documentul
   *  (inventarul, care numără obiectele NEDESENATE) o spune până atunci. */
  const [felia, setFelia] = useState<{ complet: boolean; nivel: number | null }>(
    { complet: true, nivel: null });
  /** Ținta, citită la momentul cererii, nu urmărită ca dependență: un link nou
   *  către alt element nu trebuie să reia documentul de la zero. */
  const tintaRef = useRef(tinta);
  tintaRef.current = tinta;

  const masoara = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    masoara.current = new URLSearchParams(window.location.search).has("perf");
  }, []);

  /** ETAPELE CĂII DE DEEP-LINK, cronometrate.
   *
   *  Un click pune selecția pe loc; un link din verificări trebuie s-o
   *  REZOLVE — documentul, planșeele de la cotă, ținta căutată prin ele. Planul
   *  se vede după al doilea pas, evidențierea abia după al treilea, iar între
   *  ele stă exact întârzierea reclamată. Fiecare etapă se scrie o singură
   *  dată, când se împlinește, cu timpul de la montare.
   *
   *  Măsurat, nu dedus: cele patru încercări de până acum au arătat că citind
   *  codul nu se poate ghici care pas costă.
   */
  const t0Montare = useRef(0);
  const etapeScrise = useRef<Record<string, boolean>>({});
  if (!t0Montare.current && typeof performance !== "undefined") {
    t0Montare.current = performance.now();
  }
  const etapa = (nume: string, gata: boolean) => {
    if (!masoara.current || !gata || etapeScrise.current[nume]) return;
    etapeScrise.current[nume] = true;
    // eslint-disable-next-line no-console
    console.log(`[perf] ${nume} la +${(performance.now() - t0Montare.current)
      .toFixed(0)} ms`);
  };
  const t = useT();
  const tn = useTn();
  const [eroare, setEroare] = useState("");
  // Cele două întrebări, ținute separat (v. `niveluriDistincte`). `null` = încă
  // n-a ales nimeni, deci se folosește implicitul.
  const [nivelAles, setNivelAles] = useState<number | null>(null);
  const [corpAles, setCorpAles] = useState<string | null>(null);
  /** Planul de situație e o vedere PROPRIE, nu o cotă: apare doar la
   *  disciplina lui și nu are corp. */
  const [vedeSituatia, setVedeSituatia] = useState(false);
  /** Macheta planului de situație. Separată de `vedere`, care e a planșelor de
   *  nivel: pe situație nu există «Plan» și «2D», există desenul și volumul. */
  const [machetaSit, setMachetaSit] = useState(false);
  // ARHITECTURA DE PE PLAN ÎNCĂ VINE. Butonul 3D nu se mai stinge și nu mai
  // întoarce vederea la planșă: macheta arată ce există — volumele curate ale
  // planului, apoi etajele arhitecturii imediat ce sunt scrise, apoi toată —,
  // iar butonul spune doar că se generează. Beneficiarul (24.09.2026): «nu mai
  // văd macheta 3D a planului de situație; pune butonul înapoi, ca s-o pot
  // vedea» — cu lanțul de azi, arhitectura macină minute bune după ce planul
  // s-a deschis.
  const machetaVine = !!stareMacheta && seAsteapta(stareMacheta);
  // PANOURILE MACHETEI DE SITUAȚIE, stinse la deschidere și aprinse din meniul
  // din stânga (Stefan, 23.09.2026: «make facade rules invisible by default and
  // can be triggered by a button on the left menu; same for the facade
  // sunlight validations»). Deschise din start, cele două ocupau amândouă
  // colțurile din dreapta peste o machetă la care abia te uitai.
  const [araFatada, setAraFatada] = useState(false);
  // Locurile asistentului fațadei în coloana din dreapta (v. `Asistent::loc`);
  // stare, nu ref, ca asistentul să se redeseneze când apar.
  const [locPanouAs, setLocPanouAs] = useState<HTMLDivElement | null>(null);
  const [locButonAs, setLocButonAs] = useState<HTMLDivElement | null>(null);
  // PE O SOLUȚIE DE PLAN bula din colț e a asistentului PLANULUI (Oana, 31.10.2026:
  // doar «Asistent» jos-dreapta, ca la 3D); a fațadei apare lângă ea numai cât
  // panoul fațadei e la vedere pe machetă — două bule cu același nume, una lângă
  // alta, fără panoul care să le deosebească, nu spun nimic.
  const peSituatie = solution.discipline === "site_plan";
  const fatadaLaVedere = machetaSit && araFatada;
  const locAsistent = useMemo(() => ({ panou: locPanouAs,
                                       buton: peSituatie && !fatadaLaVedere ? null : locButonAs }),
                              [locPanouAs, locButonAs, peSituatie, fatadaLaVedere]);
  const [locButonPlan, setLocButonPlan] = useState<HTMLDivElement | null>(null);
  const locAsistentPlan = useMemo(() => ({ panou: locPanouAs, buton: fatadaLaVedere ? locButonPlan : locButonAs }),
                                  [locPanouAs, locButonPlan, locButonAs, fatadaLaVedere]);
  const [araInsorire, setAraInsorire] = useState(false);
  // SCHIȚA DE VOLUMETRIE din care a ieșit planul de situație, ca fantomă
  // (8.10.2026). Comutatorul apare numai când soluția poartă o schiță.
  const [araSchita, setAraSchita] = useState(false);
  /** Căile de evacuare ale subsolului (traseul cel mai lung al fiecărei ieșiri
   *  SAS și coridoarele lor), APRINSE la deschidere (Oana, 20.10.2026; până
   *  atunci stinse). Butonul apare doar când nivelul le are. Se DESENEAZĂ abia
   *  după ce planșa de parcaj a ajuns pe ecran (`locuriDesenate`): traseele
   *  verzi apăreau cu secunde înaintea locurilor, cât se încărca subsolul. */
  const [araEvacuare, setAraEvacuare] = useState(true);
  /** Semnele de circulație ale subsolului (`diagnostics.semne_circulatie`), un
   *  strat al planșei, aprins implicit (Oana, 21.10.2026); ca evacuarea, se
   *  desenează după locuri. */
  const [araCirculatie, setAraCirculatie] = useState(true);
  const [locuriDesenate, setLocuriDesenate] = useState(false);
  // ȘI RIGLA SOARELUI, tot din meniu. Beneficiarul (24.09.2026): «vreau să fie
  // mereu lumina pe mijlocul zilei și s-o ascunzi; un buton ca la regulile de
  // fațadă, și când îl apeși apare meniul de lumină». Lumina e la amiază și cu
  // rigla ascunsă (v. `ceas`) — rigla e pentru cine vrea să mute soarele.
  const [araSoare, setAraSoare] = useState(false);
  /** HARTA ORAȘULUI în jurul lotului, pe planul de situație și pe macheta lui
   *  (v. `viewer/oras/`): pornită de la deschidere — lotul se citește în
   *  orașul lui. */
  const [araOras, setAraOras] = useState(true);
  const [vedereaSit, setVedereaSit] =
    useState<"ansamblu" | "strada" | "curte" | "pasare">("ansamblu");
  /** Cotele așezării, pe ecran. STINSE la pornire, spre deosebire de planșa
   *  tipărită, care le poartă întotdeauna (v. `SitePlanView.arataCote`).
   *
   *  Pe hârtie cotele sunt jumătate din ce e de citit, dar pe ecran omul se
   *  uită întâi la formă, iar patruzeci de cifre roșii peste nouă corpuri
   *  acoperă tocmai forma. Aici sunt un instrument de verificare: se aprind
   *  când vrei să măsori, nu stau aprinse cât te uiți. */
  const [coteSit, setCoteSit] = useState(false);
  /** Secțiunea «Suprafețe» din meniul din stânga, desfăcută sau nu. */
  const [suprafeteDeschise, setSuprafeteDeschise] = useState(false);
  const [generaleDeschise, setGeneraleDeschise] = useState(false);
  /** Planul de situație sub planșa de nivel, ca fundal.
   *
   *  Un etaj desenat singur nu spune ÎNCOTRO dă fiecare apartament: cadrul e al
   *  clădirii, nu al terenului, iar sudul unui balcon nu se vede din nimic.
   *  Pus peste sit, fiecare fațadă capătă strada, curtea și vecinul din fața ei.
   *
   *  Stins la pornire: e o verificare de orientare, nu un strat al planșei. */
  const [sitSubPlan, setSitSubPlan] = useState(false);
  /** Ceasul machetei: minutul din zi și ziua din an.
   *
   *  PORNEȘTE LA AMIAZĂ, nu la ora de acum: `minut: null` înseamnă amiaza
   *  SOLARĂ a locului în ziua aleasă (v. `soarele`), adică soarele cel mai sus
   *  și umbrele cele mai scurte. Pornită pe ceasul calculatorului, macheta
   *  deschisă seara ieșea în umbră, iar noaptea neagră — o clădire care nu se
   *  vede, deși nu era nimic de văzut greșit. Ziua rămâne cea de azi. */
  const [ceas, setCeas] = useState<{ an: number; luna: number; zi: number;
                                     minut: number | null }>(
    () => ({ ...acumLa(), minut: null }));
  /** Anotimpul ALES, ca stare. Dedus din dată, «azi» se aprindea odată cu
   *  «21 mar» chiar pe 21 martie, iar comparația chema ceasul la fiecare cadru
   *  doar ca să se măsoare cu el însuși. */
  const [anotimp, setAnotimp] = useState<"azi" | "mar" | "iun" | "dec">("azi");
  /** Documentul de ARHITECTURĂ înlănțuit pe planul ăsta de situație.
   *
   *  De ce e nevoie de el: planul de situație n-are niciun `Window` — disciplina
   *  lui decide amplasarea, nu fațada. Golurile stau în arhitectură, iar
   *  documentul ei conține ȘI stratul de situație, în același sistem de
   *  coordonate, deci nu se lipesc două documente: se citește al doilea.
   *
   *  Se aduce DOAR când se deschide macheta — sunt 23.864 de obiecte, iar cine
   *  se uită la planșa 2D n-are nevoie de ele. */
  // DOCUMENTUL DE ARHITECTURĂ, BRUT și orientat. Se ține brut ca fațada
  // previzualizată — obiecte venite în cadrul documentului — să se poată pune
  // peste el înainte de orientare, prin același `northUp` ca restul.
  const [docArhBrut, setDocArhBrut] = useState<CanonicalDoc | null>(null);
  const [arhId, setArhId] = useState<string | null>(null);
  const docArh = useMemo(() => (docArhBrut ? northUp(docArhBrut) : null), [docArhBrut]);
  /** Documentul PLANULUI DE SITUAȚIE PĂRINTE, brut, pe o arhitectură. Stratul
   *  de la sol al arhitecturii e o copie luată când a pornit jobul ei — de la
   *  pasul 1 al planului —, iar pasul 2 (pregătirea) și agentul (circulația
   *  și amenajările) rescriu doar rândul planului. Deschisă o arhitectură pe «Plan de
   *  situație», se vedea deci planul vechi. Stratul se ia din părinte
   *  (`lineage.site_plan`), care e singurul care îl decide. */
  const [docSitBrut, setDocSitBrut] = useState<CanonicalDoc | null>(null);
  const setDocArh = useCallback((d: CanonicalDoc | null) => setDocArhBrut(d), []);
  /** Același lucru pe o ARHITECTURĂ deschisă direct: documentul ei e deja
   *  orientat (`northUp` la încărcare), deci obiectele venite brute se orientează
   *  și ele, o dată, prin aceeași funcție — pe un document făcut doar din ele. */
  /** Fațada previzualizată se pune PESTE document: obiectele de fațadă vechi
   *  pleacă, cele noi intră; ce vine cu același id (ferestrele cu tâmplăria
   *  nouă, corpurile cu cantitățile) ÎNLOCUIEȘTE; restul (pereți, camere,
   *  planșee) rămâne. */
  const cuFatada = (d: CanonicalDoc, obiecte: CanonicalDoc["objects"]): CanonicalDoc => {
    const FAT = new Set(["Facade", "FacadePanel", "Railing"]);
    const noi = new Map(obiecte.map((o) => [o.id, o]));
    const ramase = (d.objects ?? []).filter((o) => !FAT.has(o.type));
    // …căutând id-urile rămase într-un SET, nu cu `some` pe listă: o fațadă
    // previzualizată aduce ~9.000 de obiecte peste un document de ~21.000, iar
    // căutarea liniară făcea 190 de milioane de comparații — secunde bune cu
    // firul principal blocat, pe un ecran care tocmai spusese «gata».
    const idRamase = new Set(ramase.map((o) => o.id));
    return { ...d, objects: [...ramase.map((o) => noi.get(o.id) ?? o),
                             ...obiecte.filter((o) => !idRamase.has(o.id))] };
  };
  const puneFatadaProprie = useCallback((obiecte: unknown[]) => {
    setDoc((d) => {
      if (!d) return d;
      const orientate = northUp({ ...d, objects: obiecte as CanonicalDoc["objects"] }).objects;
      return cuFatada(d, orientate);
    });
  }, []);
  const puneFatada = useCallback((obiecte: unknown[]) => {
    setDocArhBrut((d) => (d ? cuFatada(d, obiecte as CanonicalDoc["objects"]) : d));
  }, []);
  /** INSTANTANEELE MACHETEI pentru anularea și refacerea fațadei (v.
   *  `MachetaFatadei`): starea documentului așa cum o ține vizualizatorul —
   *  brut pe situație, orientat pe arhitectură —, pusă înapoi tot așa. Aceeași
   *  scenă, aceeași cameră (`RandareSit3D` o ține peste reconstrucții), fără
   *  pânză goală între ele. */
  const docRef = useRef(doc);
  docRef.current = doc;
  const feliaRef = useRef(felia);
  feliaRef.current = felia;
  const docArhBrutRef = useRef(docArhBrut);
  docArhBrutRef.current = docArhBrut;
  const pePeArhitectura = solution.discipline === "architecture";
  const machetaFatadei = useMemo<MachetaFatadei>(() => ({
    instantaneu: () => (pePeArhitectura
      ? (feliaRef.current.complet ? docRef.current : null)
      : docArhBrutRef.current),
    restabileste: (d) => {
      if (!d) return;
      if (pePeArhitectura) setDoc(d as CanonicalDoc);
      else setDocArhBrut(d as CanonicalDoc);
    },
    // pe situație soluția de pe ecran e planul, care nu se schimbă: nimic de înghițit
    urmeazaScriere: () => { if (pePeArhitectura) fatadaMoale.current = Date.now(); },
  }), [pePeArhitectura]);
  // ANULAREA ȘI REFACEREA NU IES DIN PAGINĂ: altă soluție sau ieșirea din
  // vizualizator le golesc, cu instantaneele lor (zeci de MB) cu tot.
  useEffect(() => () => golesteAnularileFatadei(), [solution.id]);
  /** Documentul de arhitectură se aduce greu (23.864 de obiecte), deci există o
   *  secundă în care încă nu e aici. Starea asta trebuie DEOSEBITĂ de lipsa lui:
   *  panoul spunea, în intervalul ăla, «fără arhitectură înlănțuită» — adică
   *  afirma o absență în timp ce răspunsul era pe drum. */
  const [arhSeIncarca, setArhSeIncarca] = useState(false);
  /** Arhitectura adusă e ÎNCĂ ÎN LUCRU. Jobul își rescrie soluția pe parcurs
   *  (etajele la pasul 1/2, apoi subsolurile), deci copia adusă e cea de la
   *  ora aducerii — poate fără goluri, poate fără ultimul corp. Fără steagul
   *  ăsta, o machetă deschisă devreme rămânea cu prima copie toată sesiunea,
   *  iar omul citea o clădire fără ferestre ca pe un model stricat. */
  const [arhInLucru, setArhInLucru] = useState(false);
  // CE FACE JOBUL ARHITECTURII ACUM, din registrul sondat la 3 s cât rulează
  // ceva: agentul de fațadă își scrie pasul pe job («gândesc stilul (turul
  // 3)», «randez 5 imagini»), iar omul care se uită la machetă vrea să știe
  // dacă ce vede e gata sau încă se lucrează la ea (Cătălin, 28.09.2026).
  const registruViu = useRegistruOptional();
  const rulareaArh = useMemo(() => {
    if (solution.discipline !== "site_plan") return null;
    const r = registruViu?.registru?.solutii.find((s) => s.discipline === "architecture"
      && s.lineage?.site_plan === solution.id && s.rulare);
    return r?.rulare ?? null;
  }, [registruViu?.registru, solution.discipline, solution.id]);
  const [selectie, setSelectie] = useState<Selection | null>(null);
  // apartamentele evidențiate pe plan de panoul de recompartimentare (oglinzile)
  const [evidentiate, setEvidentiate] = useState<ReadonlySet<string> | null>(null);
  const evidentiaza = useCallback((ids: string[] | null) =>
    setEvidentiate(ids && ids.length ? new Set(ids) : null), []);
  /** RECOMPARTIMENTAREA PE PLAN (v. `RecompPanou`): variantele răsfoite în panou și cele
   *  aplicate care încă se scriu — desenate peste apartamentele lor până când documentul
   *  recitit le are. */
  const [recompRasfoite, setRecompRasfoite] = useState<VariantaPePlan[] | null>(null);
  const [recompAplicate, setRecompAplicate] = useState<(VariantaPePlan & { rang: number })[]>([]);
  /** Cât se scrie o variantă aplicată, o schimbare de `updated_at` a soluției NU mai
   *  trece prin efectul de deschidere (care golește pânza, camera, selecția), ci prin
   *  recitirea de dedesubt. `job` cât rulează; `gata` = jobul s-a terminat și documentul
   *  s-a recitit deja, deci următoarea schimbare (registrul o aduce după) se înghite. */
  const recompMoale = useRef<{ faza: "job" | "gata"; la: number } | null>(null);
  /** O SCRIERE A PANOULUI DE FAȚADĂ (stil, compoziție, anulare, refacere) pune
   *  `updated_at` pe rând, iar registrul recitit o aduce pe soluție. Pe o
   *  arhitectură deschisă direct, schimbarea aceea trecea prin efectul de
   *  deschidere — pânza golită, camera la preset —, deși documentul se
   *  recitește oricum peste ce e pe ecran (sau e deja pe ecran, ca instantaneu).
   *  Ține `la`; prima schimbare de după se înghite. */
  const fatadaMoale = useRef<number | null>(null);
  /** Pe situație: documentul arhitecturii de pe ecran e deja cel scris (un
   *  instantaneu pus la loc de anulare/refacere) — următoarea întrebare a
   *  registrului doar își notează cheia, nu-l mai aduce. */
  const adoptaCheiaArh = useRef(false);
  const recompVerifica = useRef(false);
  /** De câte ori s-a cerut recitirea documentului — după o scriere din
   *  editorul de nivel (v. `EditorNivel`). Recitirea NU trece prin efectul de
   *  deschidere: acela golește pânza, cota și corpul aleși, iar cine tocmai a
   *  salvat etajul 3 vrea să rămână pe etajul 3, cu masa în starea ei. */
  const [reciteste, setReciteste] = useState(0);
  /** SELECȚIA DE PE MACHETĂ (12.10.2026): elementele alese cu clic (Shift = mai
   *  multe) și zonele trase cu ⌥/Alt; pleacă la asistentul de fațadă. Ține cât
   *  stai pe soluție. */
  const [selMacheta, setSelMacheta] = useState<{ elemente: string[]; zone: ZonaMacheta[] }>(
    { elemente: [], zone: [] });
  useEffect(() => { setSelMacheta({ elemente: [], zone: [] }); }, [solution.id]);
  /** ETAJUL TĂIAT pe macheta de situație (v. `plan/taieturaEtaj.ts`); `null` =
   *  toată clădirea. Ține cât stai pe soluție. */
  const [etajMacheta, setEtajMacheta] = useState<number | null>(null);
  useEffect(() => { setEtajMacheta(null); }, [solution.id]);
  /** Cipul de sub mouse în asistent: macheta îl aprinde mai tare. */
  const [aprinsMacheta, setAprinsMacheta] = useState<string | null>(null);
  /** Elementul arătat în panoul de detalii și cotat pe machetă: cel ales
   *  ultimul, până când omul trece la altul cu săgețile panoului. */
  const [activMacheta, setActivMacheta] = useState<string | null>(null);
  const [coteMacheta, setCoteMacheta] = useState(true);
  const eidActiv = activMacheta && selMacheta.elemente.includes(activMacheta) ? activMacheta
    : (selMacheta.elemente[selMacheta.elemente.length - 1] ?? null);
  const alegePeMacheta = useCallback((eid: string | null, adauga: boolean) => {
    if (eid) setActivMacheta(eid);
    setSelMacheta((v) => {
      if (!eid) return adauga ? v : { elemente: [], zone: [] };
      if (!adauga) return { elemente: [eid], zone: [] };
      return { ...v, elemente: v.elemente.includes(eid) ? v.elemente.filter((e) => e !== eid)
                                                        : [...v.elemente, eid] };
    });
  }, []);
  const zonaPeMacheta = useCallback((z: ZonaMacheta, adauga: boolean) => {
    setSelMacheta((v) => (adauga ? { ...v, zone: [...v.zone, z] } : { elemente: [], zone: [z] }));
  }, []);
  const { potScrie } = useRolProiect();
  // Straturile STINSE ale planului de arhitectură. Ține de OM, nu de nivel:
  // cine a stins structura arhitectului ca să citească compartimentările vrea
  // s-o vadă stinsă și la etajul următor, nu s-o stingă din nou la fiecare
  // buton.
  //
  // Toate APRINSE, fiindcă niciunul nu mai redesenează ce e sub el.
  //
  // Două porneau stinse cât timp „Interioare" desena și pereți, și camere, și
  // etichete — peste cele din plan, la alte grosimi. Odată ce fiecare strat
  // aduce exact ce nu are altul (mobilier, nuclee, circulație, planșa de
  // parcaj), nu mai există motiv să fie ascuns la deschidere: un desen complet
  // e răspunsul bun la „arată-mi nivelul".
  // NUMELE CAMERELOR ȘI COTELE SUNT APRINSE, ca pe tronson (Stefan,
  // 23.09.2026: «direct numele camerei la fel cum e scris și la tronsoane»,
  // «cotele cum sunt în tronsoane»). Până atunci planul se citea prin cifra din
  // cerc și o cheie de legendă, iar cotele porneau stinse fiindcă scriau peste
  // o mie de cifre; cotele tronsonului aleg singure ce se scrie (v.
  // `tronson/cote.ts`), deci pot sta aprinse.
  //
  // Rămân stinse, ÎN INTERIORUL cotelor, cele două rânduri dese ale planului
  // vechi — golurile și camerele — care se mai desenează doar pe parcaj.
  const [ascunse, setAscunse] = useState<ReadonlySet<Strat>>(
    () => new Set<Strat>(["cote_goluri", "cote_camere",
                          "apartamente",
                          // proiecția altui nivel: se aprinde când se caută
                          // un loc pentru o pilă, nu la fiecare deschidere
                          "dedesubt"]));
  /** Care din cele trei vederi e pe ecran — v. `selectorMod`.
   *
   *  `plan` și `2d` sunt amândouă DESENUL (SVG), în cele două convenții; `3d`
   *  e macheta. `macheta` rămâne derivat din ea, ca restul ecranului să
   *  întrebe mai departe „se vede desenul sau volumul?" fără să afle de
   *  convenții. */
  const [vedere, setVedere] = useState<"plan" | "3d">("plan");
  /** S-a cerut masa de editare? V. `nivelDeEditat`. */
  const [editez, setEditez] = useState(false);
  /** S-a cerut editarea PARCAJULUI? V. `edParcaj` — pe desenul subsolului, nu
   *  pe masă (v. `parcaj/useEditorParcaj`). */
  const [editezParcaj, setEditezParcaj] = useState(false);
  /** Planșa de linie, pe hârtie albă sau pe fond negru. Ține doar de «Plan»:
   *  „2D" e o reprezentare cu tonuri, care pe negru n-ar mai fi ea. */
  const [noapte, setNoapte] = useState(false);
  const macheta: Vedere3 | null = vedere === "3d" ? "3d" : null;
  const conventie: Conventie = vedere !== "plan" ? "randat"
                             : noapte ? "tehnic_noapte" : "tehnic";
  const planNoapte = vedere === "plan" && noapte;
  const svgRef = useRef<SVGSVGElement>(null);
  useDocumentSolutie({
    projectId, solution, doc, setDoc, felia, setFelia, setEroare, reciteste,
    setReciteste, tintaRef, recompMoale, fatadaMoale, setSelectie, setNivelAles,
    setCorpAles, setVedeSituatia, setDocArh, setDocSitBrut, setArhSeIncarca,
    // declarate mai jos; chemate doar din efect, după randare
    mostenesteCamera: () => mostenesteCamera(),
    uitaArhitecturaAdusa: () => { arhAdusa.current = null; arhCheie.current = null; },
    t,
  });

  /** Originea locală a desenului, și documentul adus la ea.
   *
   *  V. `canonical.laOrigineLocala`: cu coordonate Stereo-70 absolute, pânza se
   *  albea peste 57 px/m, adică SUB `K_MAX`. Se face o singură dată pe document
   *  — nu la fiecare desenare — fiindcă e o parcurgere peste toate obiectele
   *  (13 171 pe un proiect real). */
  const origine = useMemo(() => (doc ? origineDesen(doc) : [0, 0] as [number, number]), [doc]);
  /** Volumele schiței de pe soluția asta (`diagnostics.schita`), dacă e. */
  const schitaVolumetrie = useMemo(() => {
    const v = (doc?.diagnostics?.schita as { volume?: { id: string; poly: number[][] }[] } | undefined)?.volume;
    return Array.isArray(v) && v.length ? v.filter((x) => Array.isArray(x?.poly) && x.poly.length >= 3) : null;
  }, [doc]);
  const docLocal = useMemo(
    () => (doc ? laOrigineLocala(doc, origine) : null), [doc, origine]);
  const model = useMemo(() => {
    const doc = docLocal;
    if (!doc) return null;
    // Traducerea poate crăpa pe un document parțial; un desen lipsă e mai bun
    // decât un ecran alb fără explicație.
    try {
      const planuri = toPlans(doc);
      const niveluri = toStoreyPlans(doc);
      const site = toSiteLayer(doc);
      // Un document de plan de situație are și el planșee — volumele au
      // niveluri — dar pe ele nu e desenat nimic: etapa aia decide amplasarea,
      // nu interioarele. Listate, erau butoane care duceau la un contur gol.
      const cote = solution.discipline === "site_plan"
        ? [] : niveluriDistincte(niveluri);
      // INVENTARUL, din chiar modelele de mai sus. Se dau toate trei fiindcă
      // vizualizatorul are trei randoare, iar ce nu trece prin niciunul e exact
      // ce panoul are de spus — v. `inventarDocument`.
      //
      // Etajele se construiesc AICI pentru toate planșeele, nu se ia
      // `etajeActive` (care e al cotei alese): întrebarea inventarului e „arată
      // vizualizatorul obiectul ăsta VREODATĂ", iar cu etajele cotei curente
      // cele 400 de uși ale documentului — desenate din tocuri de
      // `ApartmentView`, la etajul lor — ar fi raportate drept nedesenate de
      // fiecare dată când te uiți la subsol.
      //
      // COSTUL, măsurat pe un document real de 5 505 obiecte cu 18 planșee:
      // 24,5 ms etajele + 1,8 ms inventarul, peste cele 40,7 ms pe care
      // `toPlans` și `toStoreyPlans` le costau oricum. O dată pe document — e în
      // același `useMemo` — nu la fiecare desenare.
      const etaje = storeysOf(doc)
        .map((st) => toApartmentLevelForStorey(doc, st.id))
        .filter((e): e is NonNullable<typeof e> => e != null);
      const inventar = inventarDocument(doc, niveluri, etaje, planuri);
      // Registrul cifrelor, când arhitectura l-a scris (v. `registrulDocumentului`).
      const registru = registrulDocumentului(doc);
      return { planuri, niveluri, site, cote, inventar, registru };
    } catch {
      return null;
    }
  }, [docLocal, solution.discipline]);
  /** Cifrele cotelor elementului activ, din document (golul, nu tâmplăria desenată). */
  const coteValoriMacheta = useMemo(
    () => (eidActiv ? dimensiuniCote(docArh, eidActiv) : undefined),
    [eidActiv, docArh]);
  /** Coloanele de apartamente pe fețe: marginile zonei trase pe machetă se
   *  lipesc de ele. */
  const coloaneMacheta = useMemo(
    () => coloaneleFetelor(docArh),
    [docArh]);

  /** Stratul de la sol al PĂRINTELUI, în cadrul desenului curent: aceeași
   *  orientare (`northUp`) și aceeași origine locală ca documentul deschis,
   *  altfel aleile planului ar cădea la un kilometru de clădirile lor. */
  const sitParinte = useMemo(() => {
    if (!docSitBrut) return null;
    try {
      return toSiteLayer(laOrigineLocala(northUp(docSitBrut), origine));
    } catch {
      return null;
    }
  }, [docSitBrut, origine]);
  /** Stratul de la sol care se ARATĂ: al părintelui pe o arhitectură (planul
   *  la zi, cu pasul 2), altfel al documentului însuși. Până vine părintele,
   *  copia din arhitectură ține locul — e planul de la pasul 1, nu un gol. */
  // …coborât la ce a construit ARHITECTURA care îl îmbracă (v. `regimulArhitecturii`)
  const siteActiv = useMemo(() => {
    const strat = sitParinte ?? model?.site ?? null;
    if (!strat || !docArh) return strat;
    try {
      return regimulArhitecturii(strat, laOrigineLocala(docArh, origine));
    } catch {
      return strat;
    }
  }, [sitParinte, model, docArh, origine]);

  const vedeStrat = (st: Strat) => !ascunse.has(st);
  // FILMUL APRINS ASCUNDE PLANȘA FINITĂ (Oana, 15.09.2026). Cadrul se desena
  // PESTE locurile și aleile soluției finale, și nu se mai vedea ce a făcut
  // pasul: locurile albe de dedesubt erau ale planului terminat, nu ale
  // pasului. Cu un cadru ales, `PlanView` primește planul FĂRĂ locuri și fără
  // alei — rămân platelajul, rampele, iar din `StoreyView` stâlpii, pereții și
  // nucleele, adică exact obstacolele după care s-a așezat pasul. Stins filmul,
  // planșa finită se întoarce neatinsă.
  const filmAprins = cadruFilm != null;

  // Cota implicită e PARTERUL, fiindcă așa se deschide orice dosar de
  // arhitectură: planul parterului e desenul pe care se citește intrarea,
  // nucleul și relația cu strada, și de la el se urcă sau se coboară. Cea mai
  // de jos cotă — un parcaj cu 17 locuri — răspundea la o întrebare pe care
  // nimeni n-o pune deschizând o soluție. Corpul implicit e «toate», adică
  // planșa de etaj a ansamblului.
  // O țintă venită din afară alege și COTA, nu doar selecția: un apartament de
  // la etajul 3, selectat pe planul parterului, ar fi o selecție corectă pe un
  // desen în care nu se vede.
  //
  // DERIVATĂ, nu stare pusă dintr-un efect. Aici a fost `vedereTintei`, în
  // aceeași expresie cu `vedereAleasa`; tradusă ca `useEffect` + `setNivelAles`
  // s-a rupt, fiindcă implicitul se calculează în render și câștigă la prima
  // desenare, iar efectul mai și scria în starea aleasă de om. Calculată aici,
  // nu există fereastră în care ținta să nu conteze.
  //
  // După `nivelAles`: o cotă apăsată cu mâna rămâne apăsată. Nu se bat, fiindcă
  // deeplinkul intră pe o pânză proaspătă, unde `nivelAles` e null.
  //
  // Tăcută dacă documentul n-are cota cerută — `planseeActive` ar ieși goală și
  // pânza albă.
  const nivelTintei = tinta
    && (model?.cote ?? []).some((c) => c.level === tinta.level)
    ? tinta.level : null;
  const nivelCerut = nivelInitial != null
    && (model?.cote ?? []).some((c) => c.level === nivelInitial) ? nivelInitial : null;
  // UN CADRU DE FILM CU NIVELUL LUI (pasul ALA, pe ultimul nivel) îl arată pe
  // acela cât e ales; stins, se întoarce la nivelul de dinainte.
  const nivelFilm = cadruFilm?.nivel != null
    && (model?.cote ?? []).some((c) => c.level === cadruFilm.nivel) ? cadruFilm.nivel : null;
  const nivelActiv = nivelFilm ?? nivelAles ?? nivelTintei ?? nivelCerut ?? nivelImplicit(model?.cote ?? []);
  /** Traseele de evacuare ale nivelului activ (`diagnostics.evacuare.niveluri`,
   *  scrise de `architecture/evacuare/pas.py`, un rând pe nivel de subsol). */
  const traseeEvacuare = useMemo(() => {
    const niv = (doc?.diagnostics?.evacuare as
      { niveluri?: { level: number; trasee?: TraseuEvacuare[] }[] } | undefined)?.niveluri;
    if (!Array.isArray(niv)) return null;
    const t = niv.filter((n) => n.level === nivelActiv).flatMap((n) => n.trasee ?? []);
    return t.length ? t : null;
  }, [doc, nivelActiv]);
  /** LOCURILE FĂRĂ RUTĂ sub prag ale nivelului activ, scrise de recalcularea
   *  căilor de evacuare (`fara_ruta`, id-urile din document): roșu pe planșă,
   *  fiindcă altfel o abatere de la NP 127 ar fi o cifră într-un raport. */
  const faraRutaEvacuare = useMemo(() => {
    const niv = (doc?.diagnostics?.evacuare as
      { niveluri?: { level: number; fara_ruta?: string[] }[] } | undefined)?.niveluri;
    if (!Array.isArray(niv)) return null;
    const ids = new Set(niv.filter((n) => n.level === nivelActiv).flatMap((n) => n.fara_ruta ?? []));
    return ids.size ? ids : null;
  }, [doc, nivelActiv]);
  /** Semnele de circulație ale nivelului activ (`diagnostics.semne_circulatie.niveluri`,
   *  scrise de `architecture/circulatie/semne.py`, un rând pe nivel de subsol;
   *  `diagnostics.circulatie` e ALTCEVA: raportul circulației planului de situație). */
  const semneCirculatie = useMemo(() => {
    const niv = (doc?.diagnostics?.semne_circulatie as
      { niveluri?: { level: number; semne?: SemnCirculatie[] }[] } | undefined)?.niveluri;
    if (!Array.isArray(niv)) return null;
    const s = niv.filter((n) => n.level === nivelActiv).flatMap((n) => n.semne ?? []);
    return s.length ? s : null;
  }, [doc, nivelActiv]);
  etapa("document citit", !!doc);
  etapa("nivelul țintei ales", nivelTintei != null);
  const corpuri = useMemo(() => (
    model && nivelActiv != null ? corpuriLaCota(model.niveluri, nivelActiv) : []
  ), [model, nivelActiv]);
  // Corpul ales rămâne ales cât timp EXISTĂ la cota curentă. Un C5 care se
  // termină la etajul 3 nu are voie să golească ecranul la etajul 4: acolo se
  // cade pe «toate corpurile», care e întotdeauna un răspuns.
  const corpActiv = corpAles && corpuri.some((c) => c.id === corpAles)
    ? corpAles : TOATE_CORPURILE;

  // PLANȘEELE desenate: unul, sau toate cele de la cota asta.
  //
  // Aici era un singur planșeu, iar de la asta veneau două neajunsuri deodată:
  // «arată-mi tot parterul» nu se putea cere, și stratul de apartamente —
  // căutat pe COTĂ, nu pe planșeu — aducea corpuri care nu erau desenate.
  const planseeActive = useMemo(() => (
    (model?.niveluri ?? []).filter((n) => n.level === nivelActiv
      && (corpActiv === TOATE_CORPURILE || n.owner === corpActiv))
  ), [model, nivelActiv, corpActiv]);

  /** Planșeul imediat INFERIOR al aceluiași corp — pentru stratul «Nivelul de
   *  dedesubt». Pe cotă și pe corp, nu pe indice: două corpuri au fiecare
   *  parterul lor, iar sub parterul lui C1 stă subsolul lui C1. `null` sub
   *  ultimul nivel de jos. */
  const dedesubtDe = useCallback((n: StoreyPlan): StoreyPlan | null => {
    let jos: StoreyPlan | null = null;
    for (const m of model?.niveluri ?? []) {
      if (m.level < n.level && (m.owner ?? null) === (n.owner ?? null)
          && (!jos || m.level > jos.level)) {
        jos = m;
      }
    }
    return jos;
  }, [model]);

  // Planul de arhitectură se desenează pe ORICE planșeu: e desenul, nu una din
  // trei alternative.
  const arhActiv = planseeActive[0] ?? null;
  /** STRATUL ISU («Rezistență la foc»), cu implicitul NIVELULUI, nu al
   *  desenului: aprins la subsol, stins la etaje (Oana, 20.10.2026). Bifa
   *  omului se ține pe fiecare familie de niveluri în parte, ca stingerea lui
   *  la parter să nu stingă și subsolul — de asta nu stă în `ascunse`, care e o
   *  singură mulțime pentru toate nivelurile. Planșa primește tot `ascunse`,
   *  cu «foc» adăugat acolo unde stratul e stins. */
  const [isu, setIsu] = useState<Record<Familie, boolean>>({ subsol: true, etaje: false });
  const vedeIsu = (n: StoreyPlan) => isu[familia(n)];
  const ascunseFaraIsu = useMemo<ReadonlySet<Strat>>(
    () => new Set<Strat>([...ascunse, "foc"]), [ascunse]);
  const isuActiv = !!arhActiv && vedeIsu(arhActiv);
  const comutaIsu = () => arhActiv
    && setIsu((v) => ({ ...v, [familia(arhActiv)]: !v[familia(arhActiv)] }));
  /** Nivelul își scrie numele camerelor și cotele cu codul tronsoanelor (v.
   *  `AdnotariNivel`)? Orice nivel de arhitectură în afară de PARCAJ: acolo
   *  locurile, aleile și rampele nu sunt ale unui tronson, iar masa nu-l
   *  deschide nici ea. */
  const cuAdnotariTronson = (n: StoreyPlan) =>
    !n.parcare && !!docLocal && solution.discipline !== "site_plan";

  /** Scena machetei. Derivată DOAR când e cerută: pe un ansamblu de șase corpuri
   *  înseamnă câteva mii de tronsoane de perete și sute de piese așezate, adică
   *  muncă degeaba pentru cine se uită la desen. */
  //  Dependența e BOOLEANUL, nu `macheta`. `macheta` e „2d" | „3d" | null, deci
  //  la comutarea 2D↔3D memo-ul se recalcula și întorcea un obiect NOU; iar
  //  `Randare3D` are `useEffect(..., [scena])`, deci demonta scena, arunca
  //  geometriile și contextul WebGL, și reconstruia totul — cu materialele și
  //  modelele luate de la capăt. Exact ce spune comentariul din `aseaza` că NU
  //  trebuie să se întâmple: vederea mută camera, nu reconstruiește. De aici
  //  venea așteptarea la fiecare apăsare pe «2D» sau «3D».
  const cuMacheta = macheta !== null;
  const scena3 = useMemo(
    () => (cuMacheta && planseeActive.length
      ? scena3D(planseeActive, docLocal, origine) : null),
    [cuMacheta, planseeActive, docLocal, origine]);
  const { arhAdusa, arhCheie, setReincearcaArh } = useArhitecturaInlantuita({
    projectId, solution, machetaSit, vedeSituatia, setDocArh, setArhId,
    setArhSeIncarca, setArhInLucru, adoptaCheiaArh,
  });

  // PLANUL PĂRINTE, pe o arhitectură. Se cere întreg și fără memorie — ca
  // arhitectura pe situație, mai sus: pasul 2 rescrie soluția sub același id,
  // iar o copie ținută ar fi arătat iar planul vechi. Dacă părintele nu se
  // poate citi, rămâne copia din arhitectură; nu e o eroare de arătat.
  useEffect(() => {
    const parinte = solution.discipline === "architecture"
      ? (solution.lineage?.site_plan ?? null) : null;
    if (!parinte) return;
    let viu = true;
    api.solutionDocument(projectId, parinte)
      .then((d) => { if (viu) setDocSitBrut(d as unknown as CanonicalDoc); })
      .catch(() => { if (viu) setDocSitBrut(null); });
    return () => { viu = false; };
  }, [projectId, solution.id, solution.discipline, solution.lineage?.site_plan]);

  /** Planșeele arhitecturii, aduse la ACEEAȘI origine locală ca situația.
   *
   *  Originea e a documentului de situație, nu a celui de arhitectură: cele
   *  două se desenează în același spațiu, iar o origine calculată separat le-ar
   *  putea rotunji la kilometri diferiți — adică ferestre la un kilometru de
   *  clădirea lor. */
  const niveluriArh = useMemo(() => {
    if (!docArh) return null;
    try {
      return toStoreyPlans(laOrigineLocala(docArh, origine));
    } catch {
      return null;
    }
  }, [docArh, origine]);

  // Ornamentele planșei de parcaj (numerotarea locurilor, aleile, rampa, grila)
  // și interiorul apartamentelor (mobilarea, nucleele, barele) rămân desenate
  // de componentele lor — dar ca STRATURI peste plan, în același cadru, nu în
  // locul lui. Se aprind din panoul de straturi ca oricare altul.
  const planuriActive = vedeStrat("parcaj")
    ? (model?.planuri ?? []).filter((pl) =>
        planseeActive.some((n) => n.id === pl.storey)) : [];
  // LOCURILE ÎNTÂI, TRASEELE DUPĂ (Oana, 20.10.2026): stratul căilor de evacuare
  // se montează abia după ce planșa de parcaj a fost DESENATĂ — două cadre după
  // ce React a așezat planșeele nivelului (primul cadru pictează locurile) — și
  // se stinge la schimbarea nivelului, ca ordinea să fie aceeași pe fiecare.
  // Cheie STABILĂ (planșeele desenate), nu lista însăși: `planuriActive` e o listă
  // nouă la fiecare randare, iar efectul pe ea s-ar reporni la nesfârșit.
  // DOAR PLANȘEELE CARE AU LOCURI, și doar când felia documentului e completă
  // pentru nivelul ăsta: felia de start aduce toate planșeele și diagnosticele
  // (deci traseele), dar locurile nivelului vin cu restul documentului — cu
  // poarta pe simpla existență a planșeului traseele apăreau tot înaintea
  // locurilor (Oana, 20.10.2026, pe VM).
  /** CALEA LOCULUI ALES (Oana, 23.10.2026: «când dau click pe un loc să-mi
   *  arate calea lui de evacuare și lungimea»), CERUTĂ LA CLIC: rutele tuturor
   *  locurilor nu stau pe document, ci în artefactul `evacuare_rute` al
   *  nivelului (`api.rutaEvacuarii`). Se cere din nou când documentul se
   *  schimbă (recalcularea căilor de evacuare îl rescrie). */
  // planșeul locului, nu primul de la cotă: cu mai multe săpături la −1 era al altui subsol
  const planulLoculuiAles = selectie?.kind === "stall" && selectie.level === nivelActiv
    ? planulSelectiei(planuriActive, selectie) : null;
  const idLoculuiAles = selectie?.kind === "stall" ? planulLoculuiAles?.spaces[selectie.i]?.id ?? null : null;
  const storeyLoculuiAles = planulLoculuiAles?.storey ?? null;
  const cuEvacuare = !!((doc?.diagnostics?.evacuare as { niveluri?: unknown[] } | undefined)?.niveluri?.length);
  const [rutaLoculuiAles, setRutaLoculuiAles] = useState<TraseuEvacuare | null>(null);
  useEffect(() => {
    setRutaLoculuiAles(null);
    if (!idLoculuiAles || !storeyLoculuiAles || !cuEvacuare) return;
    let viu = true;
    api.rutaEvacuarii(projectId, solution.id, storeyLoculuiAles, idLoculuiAles)
      .then((r) => { if (viu) setRutaLoculuiAles(traseulLocului(idLoculuiAles, r)); })
      // fără rută pe ecran, nimic altceva: clicul a ales locul, inspectorul e deschis
      .catch(() => {});
    return () => { viu = false; };
  }, [projectId, solution.id, storeyLoculuiAles, idLoculuiAles, cuEvacuare, doc]);
  const feliaGata = felia.complet || felia.nivel === nivelActiv;
  const cheiaPlanurilor = feliaGata
    ? planuriActive.filter((pl) => pl.spaces.length > 0).map((pl) => pl.storey).join("|") : "";
  useEffect(() => {
    setLocuriDesenate(false);
    if (!cheiaPlanurilor) return;
    let cadru2 = 0;
    const cadru1 = requestAnimationFrame(() => {
      cadru2 = requestAnimationFrame(() => setLocuriDesenate(true));
    });
    return () => { cancelAnimationFrame(cadru1); cancelAnimationFrame(cadru2); };
  }, [cheiaPlanurilor]);
  // Pe PLANȘEU, nu pe cotă: v. `toApartmentLevelForStorey`. Căutat pe `level`,
  // stratul aducea apartamentele TUTUROR corpurilor care ating cota, desenate
  // la coordonatele LOR — pe un ansamblu C1..C4 asta arăta ca un strat fără
  // nicio legătură cu planul de sub el, fiindcă era alt corp.
  const etajeActive = useMemo(() => (
    doc ? planseeActive
      .map((n) => toApartmentLevelForStorey(doc, n.id))
      .filter((e): e is NonNullable<typeof e> => e != null && e.flats.length > 0)
      : []
  ), [doc, planseeActive]);

  // Contextul inspectorului: planșeul selecției (cel apăsat, `storey`), altfel
  // primul desenat. Cu «toate corpurile» pot fi mai multe la aceeași cotă, iar
  // un loc, o alee sau o cameră se numesc după indexul lor pe planșeul LOR.
  const planActiv = planulSelectiei(planuriActive, selectie);

  /** Inelul unui obiect de pe planul de arhitectură, căutat pe `eid` prin toate
   *  planșeele desenate. Aceeași căutare pe care o face `gasesteObiect` pentru
   *  panou — pe `eid`, nu pe index, fiindcă la aceeași cotă stau șase planșee. */
  const inelObiectului = useCallback((eid: string): number[][] | null => {
    for (const n of planseeActive) {
      for (const familie of [n.spaces, n.walls, n.openings, n.columns,
                             n.beams, n.slabs, n.flatShapes]) {
        const o = (familie ?? []).find((x) => x.eid === eid);
        if (o?.poly && o.poly.length >= 3) return o.poly;
      }
    }
    return null;
  }, [planseeActive]);

  // ȚINTA, căutată pe TOATE planșeele de la cotă, nu doar pe primul.
  //
  // Cu «toate corpurile» la o cotă stau mai multe planșee — pe ansamblul de
  // referință, C1..C4 la parter — și toate se desenează (v. `etajeActive.map`
  // mai jos). Dar o `Selection` poartă indici (`i`, `j`) relativi la UNUL
  // singur, deci trebuie ales unul ca să se poată citi.
  //
  // Când omul dă click, alegerea o face click-ul. Pentru o țintă venită din
  // validator, nu: raportul zice «Parter · C3 · Ap. 1 · Hol tronson 2», iar
  // camera aia nu e în C1. Se căuta numai în `etajeActive[0]`, `selectionOfId`
  // întorcea null, efectul de mai jos ieșea pe `if (!sel) return` și nu se
  // selecta nimic — de unde previzualizarea GOALĂ din ecranul de verificări și
  // «Vezi pe planșă» care nu ducea nicăieri (01.09.2026).
  //
  // Pe un proiect cu un singur corp mergea, fiindcă primul planșeu era chiar
  // singurul; de asta n-a ieșit mai devreme. Reproducerea: `tintaMulticorp.test.ts`.
  // Cheia, nu obiectul, ca dependință: `Studio` face ținta cu
  // `tintaFromParam(params.get("sel"))` la FIECARE randare, deci obiectul e nou
  // de fiecare dată. Memoizat pe el, calculul de mai jos — o buclă peste toate
  // planșeele active, fiecare cu o căutare prin apartamentele și camerele lui —
  // se refăcea la orice randare a paginii, deși ținta nu se schimbase.
  const cheieTintaStabila = tinta ? tintaToParam(tinta) : null;
  const tintaRezolvata = useMemo(() => {
    if (!tinta) return null;
    // Ținta de pe planul de ARHITECTURĂ vine ca «obiect»: se caută pe `eid`
    // prin planșee, nu prin apartamente. Fără cazul ăsta, `selectionOfId`
    // cădea pe `default: null`, deci linkul din verificări deschidea planul și
    // se oprea acolo — fără selecție, fără contur, fără cadru mutat.
    if (tinta.kind === "obiect" && inelObiectului(tinta.id)) {
      const sel = { kind: "obiect", level: tinta.level,
                    eid: tinta.id } as unknown as Selection;
      const etaj = etajeActive[0] ?? null;
      return { etaj, sel };
    }
    const ref = ({ kind: tinta.kind, level: tinta.level,
                   i: -1, id: tinta.id } as unknown) as VRef;
    for (const etaj of etajeActive) {
      const sel = selectionOfId(ref, etaj);
      if (sel) return { etaj, sel };
    }
    return null;      // raport învechit: elementul nu mai e pe niciun planșeu
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cheieTintaStabila, etajeActive, inelObiectului]);

  /** CÂT DUREAZĂ O SELECȚIE, măsurat — pornit cu `?perf=1` în adresă.
   *
   *  Scris fiindcă ghicitul a costat deja două deploy-uri: întâi umbra
   *  pereților, apoi mobilierul, fiecare plauzibilă și niciuna dovedită. Un
   *  număr adevărat spune dintr-o dată dacă timpul se duce în JavaScript (între
   *  click și sfârșitul randării) sau în BROWSER (între randare și pixelul de pe
   *  ecran — adică rasterizarea filtrelor SVG, care nu apare în niciun profil de
   *  React).
   *
   *  Două rAF-uri: primul se execută înainte de pictare, al doilea după. Restul
   *  timpului până la al doilea e ce a durat pictarea.
   */


  useEffect(() => {
    if (!masoara.current || !selectie) return;
    const t0 = performance.now();
    let randat = 0;
    requestAnimationFrame(() => {
      randat = performance.now();
      requestAnimationFrame(() => {
        const pictat = performance.now();
        // eslint-disable-next-line no-console
        console.log(`[perf] selecție → randare ${(randat - t0).toFixed(0)} ms`
          + ` · randare → pictare ${(pictat - randat).toFixed(0)} ms`
          + ` · total ${(pictat - t0).toFixed(0)} ms`
          + ` · planșee ${etajeActive.length}`
          + ` · spații ${planseeActive.reduce((t, n) => t + (n.spaces?.length ?? 0), 0)}`);
      });
    });
  }, [selectie, etajeActive, planseeActive]);

  /** Inelul unei selecții, ADUS LA ORIGINEA LOCALĂ.
   *
   *  `etajeActive` se construiește din `doc`, documentul NEDEPLASAT, deci
   *  inelele lui sunt în coordonate absolute — Stereo 70, ordinul sutelor de
   *  mii de metri (v. «Centru 326082, 583087» din panou). Desenul, în schimb,
   *  lucrează în coordonate locale: `origineDesen` scade un multiplu de 1000,
   *  iar tot ce se desenează trece prin `docLocal` sau prin `adnotareInCadru`.
   *
   *  Un inel absolut folosit ca atare trimite cadrul la ~326 km de clădire —
   *  pânză goală, fără nicio eroare. Exact așa arăta previzualizarea din
   *  ecranul de verificări, și tot așa a rămas planul mare după ce încadrarea
   *  pe țintă a început să-l folosească. Conturul roșu al selecției se desena
   *  la fel de departe, deci nu se vedea niciodată.
   */
  const inelLocal = useCallback((sel: Selection | null | undefined,
                                 etaj: typeof etajActiv) => {
    // «obiect» — felul cu care planul de ARHITECTURĂ marchează tot ce se poate
    // apăsa (`StoreyView.pick`): pereți, camere, goluri, stâlpi, grinzi.
    //
    // `ringOfSelection` nu-l cunoaște: el citește un `ApartmentLevel`, adică
    // apartamente cu indici, iar un «obiect» se caută pe `eid` în PLANȘEU.
    // Consecința era că, pe planul de arhitectură, selecția umplea panoul (care
    // are propria căutare, `gasesteObiect`) dar nu desena NICIUN contur și nu
    // muta cadrul — adică exact «se deschide planul, dar nu evidențiază nimic».
    const inel = sel?.kind === "obiect"
      ? inelObiectului(sel.eid)
      : ringOfSelection(sel, etaj ?? undefined);
    if (!inel) return null;
    const [ox, oy] = origine;
    return (!ox && !oy) ? inel : inel.map(([x, y]) => [x - ox, y - oy]);
  }, [origine, inelObiectului]);

  etapa("planșee de la cotă", planseeActive.length > 0);
  etapa("planșee traduse (etajeActive)", etajeActive.length > 0);
  etapa("ținta rezolvată", !!tintaRezolvata);

  // Planșeul pe care se CITEȘTE selecția: al țintei când există, altfel primul.
  // Toți consumatorii lui `etajActiv` sunt legați de selecție —
  // `describeSelection`, `ringOfSelection`, încadrarea — deci trebuie să vadă
  // exact planșeul pe care s-au rezolvat indicii, altfel `i` și `j` s-ar citi
  // pe alt corp și panoul ar numi altă cameră.
  const etajActiv = tintaRezolvata?.etaj ?? etajeActive[0] ?? null;
  const coloane: Column[] = planuriActive.flatMap((pl) => pl.columns ?? []);

  // Conturul pe care se desenează planul de parcare e al STRUCTURII, nu al
  // lotului.
  //
  // `PlanView` desenează „deck"-ul pe conturul primit, iar deck-ul e placa
  // subsolului — adică peretele exterior al construcției. Trimițându-i lotul,
  // placa se întindea până la limita de proprietate și peretele nu se mai
  // vedea nicăieri. Conturul structurii e poligonul obiectului care DEȚINE
  // nivelul: `Storey.properties.building|area` → obiectul acela → `poly`.
  //
  // Se calculează din NIVELUL activ, nu din stratul de parcaj: încadrarea n-are
  // voie să depindă de ce bife e apăsată. Legat de `planActiv`, stingerea
  // planșei de parcaj schimba conturul pe care se încadrează desenul, deci
  // desenul SĂREA la o bifă — iar bifele sunt exact lucrul pe care omul îl
  // apasă ca să compare două straturi în același loc.
  const conturStructurii = useMemo(() => {
    if (!doc || !arhActiv) return null;
    const obiecte = (doc.objects ?? []) as Obiect[];
    const nivel = obiecte.find((o) => o.id === arhActiv.id);
    const stapan = nivel && obiecte.find((o) => o.id === proprietarul(nivel));
    const poly = stapan?.geometry?.poly;
    return poly && poly.length >= 3 ? (poly as Pt[]) : null;
  }, [doc, arhActiv]);

  // `docLocal`, nu `doc`: rezerva asta e geometrie, deci trebuie să vină din
  // documentul adus la originea locală, ca tot restul desenului. Citită din
  // documentul brut, lotul ar fi singurul lucru rămas în coordonate absolute.
  const lot: Pt[] = useMemo(() => (
    (siteActiv?.boundary ?? (docLocal?.lot?.boundary as Pt[] | undefined) ?? []) as Pt[]
  ), [siteActiv, docLocal]);

  /** Se știe unde e nordul pe desenul ăsta? V. `isGeoreferenced`. */
  const georeferenced = isGeoreferenced(
    (doc as { crs?: { epsg?: number | null } } | null)?.crs);

  // NICIO ORIENTARE AICI. Documentul vine deja în cadrul desenului, întors o
  // singură dată la citire de `northUp` — v. nota de la `setDoc`. Stratul de
  // situație se orienta cândva pe cont propriu, și exact de-aia planurile de
  // nivel ale aceluiași proiect ieșeau cu un sfert de tură față de el.
  const situatie = useMemo(() => (
    siteActiv && lot.length >= 3 ? { site: siteActiv, lot } : null
  ), [siteActiv, lot]);
  // FILMUL CIRCULAȚIEI, pe situație: cât e ales un cadru, planșa finită își
  // ascunde aleile, locurile, trotuarele și verdele — rămân corpurile, rampele,
  // excavația și limita, adică obstacolele după care s-a trasat —, iar cadrul
  // desenează ce era pus la pasul lui. Altfel planul finit stătea sub film și nu
  // se vedea cum a crescut de la zero (Oana, 29.09.2026). Ca `PlanView` fără
  // `spaces`/`lanes` la filmul parcării.
  const situatieFilm = useMemo(() => (
    situatie && cadruFilm?.situatie
      ? { ...situatie, site: { ...situatie.site, roads: [], stalls: [], sidewalks: [],
                               green: { deep: [], over_slab: [] }, entrances: [] } }
      : situatie
  ), [situatie, cadruFilm]);

  // ---------------------------------------------------------------------------
  // MACHETA DE SITUAȚIE. Trei derivări care nu se amestecă: geometria (o dată
  // per soluție), locul pe glob (o dată per lot) și soarele (la fiecare mutare
  // a riglei). Despărțite așa, tragerea de riglă nu reconstruiește scena 3D —
  // v. nota de la `cuMacheta`, unde greșeala asta a costat o așteptare la
  // fiecare apăsare de buton.
  const scenaSituatie = useMemo(
    () => (machetaSit && vedeSituatia && situatie
      ? scenaSit(situatie.site, situatie.lot, niveluriArh) : null),
    [machetaSit, vedeSituatia, situatie, niveluriArh]);
  // ORAȘUL DIN JUR, pe plan și pe machetă: o hartă, două desene. Numai în
  // Studio (`plin`), unde e și comutatorul lui; previzualizările nu-l cer.
  const orasul = useOrasInCadru({
    activ: araOras && plin && vedeSituatia && !!situatie,
    crs: doc?.crs, origine, lot: situatie?.lot,
  });
  const stratOras = useStratOras(scenaSituatie ? orasul.oras : null);
  /** Etajele machetei care au interior de arătat (o arhitectură cu planuri). */
  const etajeMacheta = useMemo(
    () => (scenaSituatie && niveluriArh?.some((p) => p.flatShapes.length) ? etajeleMachetei(scenaSituatie.fatade) : []),
    [scenaSituatie, niveluriArh]);
  const etajAles = etajMacheta !== null && etajeMacheta.includes(etajMacheta) ? etajMacheta : null;

  /** Unde e parcela pe glob — din coordonatele ei, nu dintr-un câmp de
   *  completat. Un document neorientat n-are cum să spună, și atunci interfața
   *  o zice: studiul de însorire pe coordonate implicite nu înseamnă nimic. */
  // `origine` E OBLIGATORIE: `lot` vine din `docLocal`, adică deja mutat la
  // originea locală, deci fără ea se proiectează un punct de lângă zero.
  const locul = useMemo(
    () => locSit(lot, georeferenced, origine), [lot, georeferenced, origine]);

  const soarele = useMemo(() => {
    const { an, luna, zi } = ceas;
    const fus = decalajFus(an, luna, zi);
    const e = efemeride(an, luna, zi, locul.loc, fus);
    // Rigla neatinsă = amiaza locului și a zilei, rotunjită la pasul riglei.
    // Se recalculează cu locul și cu anotimpul: amiaza din decembrie nu e
    // aceeași oră pe ceas cu cea din iunie.
    const minut = ceas.minut ?? Math.round(e.amiaza * 12) * 5;
    const p = pozitiaSoarelui(an, luna, zi, minut / 60, locul.loc, fus);
    return { ...p, fus, ...e, minut };
  }, [ceas, locul]);

  /** Studiul de însorire. Se reface doar când se schimbă GEOMETRIA sau ANUL —
   *  nu și când se mișcă rigla: norma întreabă despre solstițiul de iarnă, o zi
   *  care nu depinde de ora la care te uiți acum la machetă. */
  const insorirea = useMemo(
    () => (scenaSituatie
      ? studiuInsorire(scenaSituatie.volume, locul.loc,
                       decalajFus(ceas.an, 12, 21), ceas.an)
      : null),
    [scenaSituatie, locul, ceas.an]);


  // Conturul pe care se încadrează desenul: al structurii la parcare, al
  // NIVELULUI la planul de arhitectură, al lotului în rest.
  //
  // Ultima parte era singura, și e motivul pentru care etajele unui document
  // importat se deschideau ca pagină albă: importul nu scrie lot (`lot: null`,
  // el descrie o clădire, nu o parcelă), deci nu se aduna niciun cadru, iar
  // fără cadru nu se desenează pânza deloc — nici măcar mesajul „documentul nu
  // are geometrie". Amprenta nivelului e în document, pe `Storey.poly`.
  // Un etaj de apartamente se desenează cu toate corpurile care ajung la cota
  // lui, deci cadrul lui e al TUTUROR, nu al primului.
  const conturDesen: Pt[][] = useMemo(() => {
    // Amprentele TUTUROR planșeelor desenate. Cu «toate corpurile» cadrul e al
    // ansamblului; cu un corp ales, doar al lui — deci alegerea corpului chiar
    // apropie desenul, în loc să lase clădirea mică într-un cadru de ansamblu.
    //
    // `outline` cade pe poligonul obiectului care deține planșeul când planșeul
    // n-are unul (subsolurile generate, v. `toStoreyPlans`).
    const amprente = planseeActive
      .flatMap((n) => (n.parts?.length ? n.parts : [n.outline]) as Pt[][])
      .filter((pp) => pp.length >= 3);
    if (amprente.length) return amprente;
    if (conturStructurii) return [conturStructurii];
    if (lot.length >= 3) return [lot];
    return [];
  }, [conturStructurii, planseeActive, lot]);

  const cadru = useMemo(() => {
    const puncte = conturDesen.flat();
    if (puncte.length < 3) return null;
    const xs = puncte.map((p) => p[0]), ys = puncte.map((p) => p[1]);
    const bbox: Bbox = [Math.min(...xs), Math.min(...ys),
                        Math.max(...xs), Math.max(...ys)];
    // `lot` e conturul pe care desenează `PlanView` platelajul parcajului —
    // unul singur, al structurii de sub el.
    return { lot: conturDesen[0], bbox };
  }, [conturDesen]);

  // Ținta, rezolvată o dată pe etajul ei. `aplicata` ține minte CE țintă a fost
  // pusă, nu doar că a fost: altfel un al doilea click pe altă regulă, cu
  // aceeași pânză deja încărcată, n-ar mai muta selecția.
  const aplicata = useRef<string | null>(null);
  useEffect(() => {
    if (!cheieTintaStabila || !tintaRezolvata) return;
    if (aplicata.current === cheieTintaStabila) return;
    aplicata.current = cheieTintaStabila;
    etapa("selecție pusă", true);
    setSelectie(tintaRezolvata.sel);
  }, [cheieTintaStabila, tintaRezolvata]);

  // ------------------------------------------------------------------------
  // MACHETA ȘI PLANUL, PE ACEEAȘI SELECȚIE
  // ------------------------------------------------------------------------
  // Selecția e UNA, a vizualizatorului. Planul o produce din `data-pick`-ul
  // formei apăsate, macheta dintr-o rază; de aici încolo drumul e comun —
  // `describeSelection` și `Inspector`. Așa clickul pe aceeași cameră dă
  // același panou în ambele vederi, fiindcă chiar ESTE același panou.
  //
  // Puntea e `eid`-ul documentului: singura identitate pe care o au amândouă.
  // Macheta nu știe ce e o `Selection` (are camere, nu apartamente cu indici),
  // iar planul nu știe ce e un mesh.
  const alegeDinMacheta = useCallback((eid: string | null) => {
    if (!eid) { setSelectie(null); return; }
    const ref = ({ kind: "flatRoom", level: nivelActiv ?? 0,
                   i: -1, id: eid } as unknown) as VRef;
    for (const etaj of etajeActive) {
      const sel = selectionOfId(ref, etaj);
      if (sel) { setSelectie(sel); return; }
    }
    // Spațiile comune (holuri de nivel, nuclee) nu sunt camere de apartament,
    // deci n-au ce selecta pe plan. Se lasă selecția neatinsă, nu se golește:
    // altfel un click pe un hol ar stinge panoul deschis pe altceva.
  }, [etajeActive, nivelActiv]);

  /** `eid`-ul selecției curente, pentru conturul din machetă. Drumul invers
   *  celui de mai sus: panoul și planul lucrează cu indici, macheta cu id-uri. */
  const eidSelectat = useMemo(() => {
    if (!selectie || !etajActiv) return null;
    const s = selectie as { kind: string; i?: number; j?: number };
    if (s.kind !== "flatRoom" || s.i == null || s.j == null) return null;
    return etajActiv.flats?.[s.i]?.layout?.[s.j]?.eid ?? null;
  }, [selectie, etajActiv]);

  // Ce s-a selectat, tradus în ce se ARATĂ. Contextul e exact ce e pe ecran:
  // un index de stâlp înseamnă altceva pe alt nivel.
  // PANOUL DE RECOMPARTIMENTARE, pe un apartament care a trecut prin ea: variantele
  // păstrate și alegerea alteia (`RecompPanou`).
  //
  // Apartamentul se caută după ORICE s-a apăsat pe el, nu doar după selecția `flat`:
  // în planul de apartament clickul cade aproape mereu pe o cameră (`flatRoom`), iar
  // în planul etajului (`StoreyView`, «toate corpurile») pe un obiect al documentului
  // (`obiect` cu `eid`-ul spațiului) — același drum ca `alegeDinMacheta`, prin
  // `selectionOfId`, pe toate planșeele active, fiindcă pe «toate corpurile» camera
  // poate fi în alt corp decât `etajActiv`. Fără asta panoul apărea doar apăsând
  // exact pe peretele de despărțire, ceea ce nimeni nu face.
  const apartamentAles = useMemo(() => {
    if (!selectie) return null;
    const s = selectie as { kind: string; i?: number; level?: number; eid?: string };
    if ((s.kind === "flat" || s.kind === "flatRoom") && s.i != null)
      return etajActiv?.flats?.[s.i] ?? null;
    if (s.kind !== "obiect" || !s.eid) return null;
    for (const etaj of etajeActive) {
      const direct = (etaj.flats ?? []).find((f) => f.eid === s.eid);
      if (direct) return direct;
      const ref = ({ kind: "flatRoom", level: s.level ?? 0, i: -1, id: s.eid } as unknown) as VRef;
      const sel = selectionOfId(ref, etaj) as { i?: number } | null;
      if (sel?.i != null) return etaj.flats?.[sel.i] ?? null;
    }
    return null;
  }, [selectie, etajActiv, etajeActive]);
  // VARIANTELE APLICATE, până le aduce documentul: una iese de pe plan când apartamentul
  // ei are în document exact varianta aia (`architecture.recomp.ales`).
  useEffect(() => {
    if (!doc) return;
    const verifica = recompVerifica.current;
    recompVerifica.current = false;
    setRecompAplicate((ap) => {
      if (!ap.length) return ap;
      const scrise = new Map<string, number>();
      for (const o of doc.objects ?? []) {
        const r = (o as { props?: Record<string, { ales?: number } | undefined> }).props?.["architecture.recomp"];
        if (o.type === "Flat" && r) scrise.set(o.id, r.ales ?? 0);
      }
      const raman = ap.filter((a) => scrise.get(a.eid) !== a.rang);
      // jobul s-a terminat și documentul recitit tot nu le are: scrierea a picat
      if (verifica && raman.length) {
        arataToast(t("recomp.picat"));
        return [];
      }
      return raman.length === ap.length ? ap : raman;
    });
  }, [doc, t]);
  // JOBUL CARE LE SCRIE, urmărit cât sunt variante aplicate pe plan: când se termină,
  // documentul se recitește pe loc, fără să aștepte sondajul registrului
  const urmaresteRecomp = recompAplicate.length > 0;
  useEffect(() => {
    if (!urmaresteRecomp) return;
    let viu = true;
    const ceas = setInterval(() => {
      api.getRecompVariante(projectId, solution.id, "-").then((r) => {
        if (!viu) return;
        if (r.job) {
          // fără memorie, jobul rezolvă din nou (minute): fereastra moale ține cât el
          if (recompMoale.current) recompMoale.current.la = Date.now();
          return;
        }
        viu = false;
        clearInterval(ceas);
        recompMoale.current = { faza: "gata", la: Date.now() };
        recompVerifica.current = true;
        setReciteste((n) => n + 1);
        void registruViu?.reincarca();
      }).catch(() => undefined);
    }, 1500);
    return () => { viu = false; clearInterval(ceas); };
  }, [urmaresteRecomp, projectId, solution.id, registruViu]);
  const laAplicatRecomp = useCallback((p: VariantaPePlan[], rang: number) => {
    recompMoale.current = { faza: "job", la: Date.now() };
    setRecompAplicate((ap) => [...ap.filter((a) => !p.some((x) => x.eid === a.eid)),
                               ...p.map((x) => ({ ...x, rang }))]);
    // SELECȚIA RĂMÂNE PE APARTAMENT: o cameră aleasă din varianta veche nu mai există
    // în documentul recitit, iar panoul ar dispărea odată cu ea
    const eid = p[0]?.eid;
    setSelectie((s) => (s && s.kind === "obiect" && eid && s.eid !== eid
      ? { ...s, eid } : s));
    void registruViu?.reincarca();
  }, [registruViu]);
  /** Ce se desenează peste plan, în cadrul desenului: aplicatele, apoi răsfoitele
   *  (acestea câștigă pe același apartament). */
  const previzRecomp = useMemo(() => {
    const toate = new Map<string, VariantaPePlan>();
    for (const a of recompAplicate) toate.set(a.eid, a);
    for (const r of recompRasfoite ?? []) toate.set(r.eid, r);
    if (!toate.size || !doc) return undefined;
    const pt = punctInCadru(doc.crs, origine);
    const inel = (r: number[][]) => r.map((q) => pt(q));
    return [...toate.values()].map(({ eid, v }) => ({
      eid,
      camere: v.camere.map((c) => ({ fel: c.fel, arie: c.arie, poly: inel(c.poly) })),
      pereti: v.pereti.map(inel),
      usi: v.usi.map(inel),
      intrare: v.intrare && v.intrare_mutata_m ? inel(v.intrare) : null,
      intrareVeche: v.intrare_veche ? pt(v.intrare_veche) : null,
    }));
  }, [recompAplicate, recompRasfoite, doc, origine]);
  // NUMELE CAMERELOR VARIANTEI, scrise de același strat ca pe planul scris (v.
  // `plan/recompPreviz`)
  const docAdnotari = useMemo(
    () => (docLocal && previzRecomp ? docCuVariante(docLocal, previzRecomp) : docLocal),
    [docLocal, previzRecomp]);
  const aplicataAici = apartamentAles?.eid
    ? recompAplicate.find((a) => a.eid === apartamentAles.eid)?.rang ?? null : null;
  const panouRecomp = apartamentAles?.recomp && apartamentAles.eid
    ? <RecompPanou projectId={projectId} solutionId={solution.id} flat={apartamentAles}
                   onEvidentiaza={evidentiaza} onPrevizualizeaza={setRecompRasfoite}
                   onAplicat={laAplicatRecomp} aplicata={aplicataAici} />
    : undefined;
  const descris = useMemo(() => {
    if (!selectie) return null;
    return describeSelection(selectie, {
      plan: planActiv, columns: coloane, apt: etajActiv, site: siteActiv,
      // TOATE planșeele active, nu `arhActiv` (care e doar primul). Cu «Toate
      // corpurile» pe ecran stau șase planșee la aceeași cotă; cu unul singur în
      // context, un click pe oricare dintre celelalte cinci nu găsea obiectul și
      // panoul nu se deschidea.
      storey: planseeActive,
      // balustradele și cortina se descriu din document (fișa de pe machetă)
      doc,
    });
  }, [selectie, planActiv, coloane, etajActiv, planseeActive, siteActiv, doc]);

  // --- zoom și panoramare ---------------------------------------------------
  //
  // Modelul e cel din editorul de arhitectură, luat ca atare: SVG în PIXELI,
  // conținutul transformat cu `translate(tx ty) scale(k)`. Cu un `viewBox` în
  // metri desenul se încadrează singur, dar nu se poate nici mări, nici muta —
  // exact ce lipsea.
  //
  // O apăsare care NU s-a mutat e un click, iar ce selectează se citește din
  // desen (`data-pick`). Decizia se ia la ridicarea degetului, nu la apăsare:
  // altfel o formă care înghite `pointerdown` ca să-și ia click-ul oprește
  // panoramarea, iar deasupra unui plan acoperit de forme nu mai rămâne nimic
  // de tras.
  const CLICK_SLOP = 4;
  const [view, setView] = useState<View | null>(null);
  /** Cât e rotit desenul pe ecran, în grade, în sensul acelor de ceas.
   *
   *  Se aplică pe grupul deja panoramat și mărit, deci se rotește CE SE VEDE,
   *  nu ce e în document: geometria rămâne cum a venit, iar selecția merge mai
   *  departe — browserul face potrivirea prin transformare. */
  const [rot, setRot] = useState(0);
  /** Cutia cu scara și legenda: STRÂNSĂ la început (Oana, 23.10.2026), cu
   *  pictograma ei în colț; omul o deschide când are nevoie de ea. */
  const [legendaDeschisa, setLegendaDeschisa] = useState(false);
  const panZoom = usePanZoom({
    view, setView, rot, setRot, doc, origine, cadru, compact, selectie,
    etajActiv, tintaRezolvata, inelLocal, arhActiv, solution, corpActiv,
    vedeSituatia, machetaSit, cheieTintaStabila,
  });
  const { dim, gazda, viewRef, tragere, rotRef, pivotRef, pivot, setPivotManual,
          mostenesteCamera } = panZoom;
  /** Scara la care se alege detaliul straturilor — v. `useScaraDetaliului`. */
  const kDetaliu = useScaraDetaliului(view?.k ?? null) ?? 1;

  /** Un punct de pe ecran, în coordonatele desenului. Inversa transformării
   *  `translate(t) scale(k) rotate(rot, pivot)`. */
  // Cadrul de film, adus în cadrul desenului. Solverul scrie în coordonatele
  // documentului, deci trece prin aceeași traducere ca obiectele lui.
  const cadruFilmInCadru = useMemo<CadruFilm | null>(() => {
    if (!cadruFilm || !doc) return null;
    const la = punctInCadru(doc.crs, origine);
    const seg = (a: { x1: number; y1: number; x2: number; y2: number }) => {
      const [x1, y1] = la([a.x1, a.y1]), [x2, y2] = la([a.x2, a.y2]);
      return { x1, y1, x2, y2 };
    };
    // Direcția locului se întoarce după ACEEAȘI regulă ca tot documentul
    // (`canonical.northUp`): sfert de tură pe grilele cu northing întâi,
    // altfel oglindirea y-ului. Scrisă altfel aici ar fi exact felul în
    // care un strat iese la un sfert de tură față de planul de sub el.
    const loc = ([cx, cy, deg, forma]: [number, number, number, string]) => {
      const [x, y] = la([cx, cy]);
      const grad = northingFirst(doc.crs as { epsg?: number } | null)
        ? deg - 90 : -deg;
      return [x, y, grad, forma] as [number, number, number, string];
    };
    return {
      ...cadruFilm,
      alei: cadruFilm.alei.map((a) => ({ ...a, ...seg(a) })),
      scoase: cadruFilm.scoase.map(seg),
      spatii: (cadruFilm.spatii ?? []).map(loc),
      pierdute: (cadruFilm.pierdute ?? []).map(loc),
      noi: (cadruFilm.noi ?? []).map(loc),
      camere: (cadruFilm.camere ?? []).map((c) => ({ ...c, poly: c.poly.map((p) => la(p)) })),
      poligoane: (cadruFilm.poligoane ?? []).map((c) => ({ ...c, poly: c.poly.map((p) => la(p)) })),
    };
  }, [cadruFilm, doc, origine]);

  const laDesen = useCallback((sx: number, sy: number) => {
    const v = viewRef.current, p = pivotRef.current;
    if (!v || !p) return null;
    const a = (-rotRef.current * Math.PI) / 180;
    const ux = (sx - v.tx) / v.k - p.x, uy = (sy - v.ty) / v.k - p.y;
    return { x: ux * Math.cos(a) - uy * Math.sin(a) + p.x,
             y: ux * Math.sin(a) + uy * Math.cos(a) + p.y };
  }, []);

  /** RIGLA. Două clicuri pe desen și scrie cât e între ele.
   *
   *  Unealtă de lucru, nu de desenat: nu atinge documentul și nu se salvează.
   *  Cât e aprinsă, un clic pune un capăt în loc să selecteze — altfel același
   *  clic ar face două lucruri deodată. Tragerea rămâne panoramare, deosebită
   *  de clic prin `CLICK_SLOP`, exact ca la selecție.
   *
   *  Pe orice vizualizator plin (Oana, 23.10.2026: «îmi trebuie rigla și în
   *  viewer-ul normal»), nu doar pe ecranul de încercări; în cardul mic
   *  (`compact`) n-are loc. V. `riglaDisponibila`. */
  const [rigla, setRigla] = useState(false);
  const [masuri, setMasuri] = useState<Masura[]>([]);
  const [inCurs, setInCurs] = useState<Masura | null>(null);
  const stingeRigla = useCallback(() => {
    setRigla(false); setMasuri([]); setInCurs(null);
  }, []);

  /** Punctul de sub cursor, în metrii desenului — pentru riglă. */
  const punctul = useCallback((e: { clientX: number; clientY: number }) => {
    const el = gazda.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return laDesen(e.clientX - r.left, e.clientY - r.top);
  }, [laDesen]);

  useComenziPanZoom({ panZoom, setView, setRot, stingeRigla });

  // EDITAREA PARCAJULUI, pe desenul subsolului. Condițiile sunt ale editării
  // de nivel (v. `poateEdita`), întoarse: UN planșeu, și el de parcaj. Hook-ul
  // stă aici — înaintea pânzei și a gesturilor ei — fiindcă amândouă îl întreabă.
  const planseuParcaj = planseeActive.length === 1 && planseeActive[0].parcare
    ? planseeActive[0] : null;
  const planParcaj = planseuParcaj
    ? (model?.planuri ?? []).find((pl) => pl.storey === planseuParcaj.id) ?? null : null;
  const poateEditaParcaj = editabil && plin && !compact && solution.discipline === "architecture"
    && solution.status === "ready" && !vedeSituatia && vedere === "plan" && potScrie
    && !!planseuParcaj && !!planParcaj && !!docLocal && felia.complet && !cadruFilm;
  /** RECALCULAREA CĂILOR DE EVACUARE pe ieșirile existente (Oana, 22.10.2026),
   *  din meniul unui nivel de parcaj: secunde pe nivel, sincron; după ea
   *  documentul se recitește. */
  const [evacuareInLucru, setEvacuareInLucru] = useState(false);
  const [evacuareRaport, setEvacuareRaport] = useState<{ text: string; rau: boolean } | null>(null);
  const edParcaj = useEditorParcaj({
    activ: editezParcaj && poateEditaParcaj,
    plan: planParcaj, nivel: planseuParcaj, doc: docLocal, origine,
    projectId, solutionId: solution.id, potScrie, punctul, pxPerM: view?.k ?? 1,
    laSalvat: () => setReciteste((n) => n + 1),
    laInchide: () => { if (potPleca()) setEditezParcaj(false); },
  });

  function apasa(e: PointerEvent<SVGSVGElement>) {
    if (!view || e.button === 2) return;
    // Editorul de parcaj ia apăsarea pe un loc (îl trage); pe gol rămâne
    // panoramarea. Cu rigla aprinsă, nu: atunci locul de sub mână e doar un
    // punct de măsurat.
    if (!rigla && edParcaj.apasa(e)) { e.currentTarget.setPointerCapture(e.pointerId); return; }
    // Orice buton mută. Nicio tastă ținută apăsat, niciun buton drept: rotirea
    // are gesturile și tastele ei.
    tragere.current = { sx: e.clientX, sy: e.clientY, tx: view.tx, ty: view.ty,
                        target: e.target };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  /** Dublu-clic mută pivotul pe ce ai dat clic. De aici încolo rotirea se face
   *  în jurul lui, deci poți alege singur ce stă pe loc. */
  function dubluClic(e: PointerEvent<SVGSVGElement>) {
    const el = gazda.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const q = laDesen(e.clientX - r.left, e.clientY - r.top);
    if (q) setPivotManual(q);
  }

  function mișcă(e: PointerEvent<SVGSVGElement>) {
    if (!rigla && edParcaj.misca(e)) return;
    const d = tragere.current;
    // Rigla desenează cât miști, ÎNAINTE de garda de tragere: fără al doilea
    // capăt viu, între cele două clicuri nu se vede nimic și nu poți ținti.
    if (rigla && inCurs && !d) {
      const q = punctul(e);
      if (q) setInCurs({ a: inCurs.a, b: q });
    }
    if (!d || !view) return;
    setView({ ...view, tx: d.tx + (e.clientX - d.sx),
              ty: d.ty + (e.clientY - d.sy) });
  }

  function ridică(e: PointerEvent<SVGSVGElement>) {
    if (edParcaj.ridica()) return;
    const d = tragere.current;
    tragere.current = null;
    if (!d) return;
    if (Math.hypot(e.clientX - d.sx, e.clientY - d.sy) > CLICK_SLOP) return;
    if (rigla) {
      // Cu rigla aprinsă, clicul MĂSOARĂ și nu selectează: altfel același gest
      // ar face două lucruri, iar inspectorul s-ar deschide peste desenul pe
      // care tocmai încerci să-l măsori.
      const q = punctul(e);
      if (!q) return;
      if (!inCurs) setInCurs({ a: q, b: q });
      else { setMasuri((v) => [...v, { a: inCurs.a, b: q }]); setInCurs(null); }
      return;
    }
    // În editarea parcajului clicul alege un loc sau pune unul nou; inspectorul
    // nu se deschide.
    if (edParcaj.activ) { edParcaj.clic(e, d.target); return; }
    const noua = selectionFromTarget(d.target);
    // A doua apăsare pe același lucru îl deselectează, iar una pe pânza goală
    // închide inspectorul.
    setSelectie((veche) => (noua && !sameSelection(veche, noua) ? noua : null));
  }

  // În machetă nivelurile sunt A DOUA BARĂ, sub etape — nu un dropdown. Motivul
  // e că se comută des: un plan se citește sărind între subsol și etaje, iar un
  // meniu care se deschide costă un click în plus de fiecare dată.
  //
  // DOUĂ bare, nu una: cotele sus, corpurile dedesubt. Combinate, lista era
  // produsul lor («Parter · C1», «Parter · C2», …) — douăzeci de butoane pentru
  // două întrebări, și niciunul care să însemne «tot parterul».
  //
  // Bara corpurilor apare doar când e ceva de ales: cu un singur corp la cota
  // asta, «Toate corpurile» și «C1» sunt același desen, iar o bară cu două
  // butoane echivalente pune o întrebare care n-are două răspunsuri.
  //
  // Pe UN RÂND, nu într-o coloană, și fiecare grup cu numele lui scris în
  // versale mici deasupra butoanelor. Stivuite, cele trei bare erau trei
  // rânduri de pastile fără nume, iar «Parter» și «C2» arătau la fel: care e
  // cota și care e corpul se deducea din conținut, adică se citea de fiecare
  // dată. Eticheta răspunde o dată pentru totdeauna, și costă un rând de 9 px.
  //
  // Ordinea e a întrebărilor, nu a datelor: ce clădire, la ce cotă, desenat cum.
  const grup = (eticheta: string, bara: React.ReactNode) => (
    <span className="grup-vederi" key={eticheta}>
      <span className="lbl">{eticheta}</span>
      {bara}
    </span>
  );

  /** Un comutator al meniului: icoana și NUMELE, pe toată lățimea. Același pe
   *  toate trei vizualizările — situație, arhitectură, structură — ca un strat
   *  care se aprinde să arate la fel oriunde. */
  const comutator = (cheie: string, pornit: boolean, laClic: () => void,
                     icoana: NumeIcoana, nume: string, titlu?: string,
                     nota?: string) => (
    <button key={cheie} className={`meniu-comutator${pornit ? " on" : ""}`}
            aria-pressed={pornit} title={titlu} onClick={laClic}>
      <Icoana nume={icoana} marime={15} />
      <span>{nume}</span>
      {nota && <span className="meniu-nota">{nota}</span>}
    </button>
  );

  // `potPleca()` pe fiecare buton care schimbă ce e pe ecran: cu planul unui
  // nivel deschis în editor și nesalvat, o cotă apăsată ar arunca munca fără
  // o vorbă (v. `EditorNivel`, care își lasă întrebarea în `nescris`).
  const selectorVederi = (() => {
    const cote = model?.cote ?? [];
    // NUMAI GRUPURILE CARE PUN O ÎNTREBARE. Regula era deja scrisă pentru
    // corpuri — cu un singur corp la cotă, «Toate corpurile» și «C1» sunt
    // același desen — și acum se aplică la toate trei, fiindcă eticheta face
    // dintr-un buton singur o promisiune de alegere care nu există. Comutatorul
    // planșei de situație e cazul limpede: la disciplina lui documentul n-are
    // cote de arătat (v. `model`), deci «Niveluri» nu s-a putut apăsa niciodată.
    const grupuri = [
      !!model?.site && solution.discipline === "site_plan" && !!cote.length
        && grup(t("viz.grup.plansa"), (
          <div className="tabs niveluri">
            <button className={vedeSituatia ? "on" : ""}
                    onClick={() => { setVedeSituatia(true); setSelectie(null); }}>
              {t("viz.plan_situatie")}
            </button>
            <button className={!vedeSituatia ? "on" : ""}
                    onClick={() => { setVedeSituatia(false); setSelectie(null); }}>
              {t("viz.niveluri")}
            </button>
          </div>
        )),
      // CORPUL ȘI COTA, CA LISTE. Stefan, 23.09.2026: «make the floor number
      // and the building into dropdowns to take less space». Pastilele țineau
      // două rânduri de 600 px pe un proiect cu patru corpuri și paisprezece
      // cote; o listă ține un rând de 200, oricâte ar fi.
      // CORPUL NICIODATĂ PE SITUAȚIE (A6), nici în vederea «Niveluri»: planul
      // de situație n-are corpuri de ales, iar clădirile se văd pe el cu toate.
      solution.discipline !== "site_plan" && !vedeSituatia && corpuri.length > 1
        && grup(t("viz.grup.corp"), (
        <select className="inp meniu-select" value={corpActiv}
                title={t("viz.toate_corpurile_titlu")}
                onChange={(e) => {
                  if (!potPleca()) return;
                  setCorpAles(e.target.value); setSelectie(null);
                }}>
          <option value={TOATE_CORPURILE}>{t("viz.toate_corpurile")}</option>
          {corpuri.map((c) => (
            <option key={c.id} value={c.id}>{c.eticheta}</option>
          ))}
        </select>
      )),
      // Cotele rămân la `length`, nu `length > 1`: o singură cotă tot se NUMEȘTE
      // — «Parter» în listă spune la ce te uiți, iar asta e o legendă, nu o
      // întrebare fără două răspunsuri.
      !vedeSituatia && !!cote.length && grup(t("viz.grup.nivel"), (
        <select className="inp meniu-select" value={nivelActiv ?? ""}
                onChange={(e) => {
                  if (!potPleca()) return;
                  setNivelAles(Number(e.target.value)); setSelectie(null);
                }}>
          {cote.map((c) => (
            <option key={c.level} value={c.level}>{c.eticheta}</option>
          ))}
        </select>
      )),
    ].filter(Boolean);
    return grupuri.length ? <>{grupuri}</> : false;
  })();

  /** Comutatorul de vedere: DESENUL sau macheta, «2D» și «3D» — aceleași
   *  două nume pe situație, arhitectură și structură.
   *
   *  CE-A PLECAT (Stefan, 23.09.2026: «completely remove the middle option»):
   *  planul RANDAT — același desen în convenția de prezentare, cu materiale,
   *  mobilier și umbre. Rămâne planșa tehnică, cea în care se lucrează, și ea
   *  se cheamă acum «2D». Înainte de el a mai plecat o dată un «2D»: macheta
   *  văzută de sus, fără cote și fără selecție. */
  const selectorMod = !vedeSituatia && arhActiv && (
    <div className="tabs niveluri">
      <button className={vedere === "plan" ? "on" : ""}
              title={t("viz.vedere_2d_titlu")}
              onClick={() => setVedere("plan")}>{t("viz.vedere_2d")}</button>
      <button className={vedere === "3d" ? "on" : ""}
              title={t("viz.vedere_3d_titlu")}
              onClick={() => potPleca() && setVedere("3d")}>{t("viz.vedere_3d")}</button>
    </div>
  );

  /** Planșa de situație are și ea două reprezentări, dar nu aceleași.
   *
   *  Pe niveluri, cele trei butoane sunt DOUĂ CONVENȚII DE DESEN plus macheta.
   *  Aici nu există „planșă tehnică" și „plan de prezentare" — A01 e o singură
   *  planșă — deci alegerea e între desen și volum, și atât. Două butoane, nu
   *  trei mimate.
   *
   *  Stă în ACELAȘI loc ca «Vedere» de la niveluri, imediat sub «Planșă»:
   *  întrebarea e aceeași («ce mă uit»), deci nu are voie să-și schimbe locul
   *  când comuți disciplina. */

  const selectorModSit = vedeSituatia && !!situatie && (
    <div className="tabs niveluri">
      <button className={!machetaSit ? "on" : ""}
              title={t("viz.sit_plan_titlu")}
              onClick={() => { setMachetaSit(false); setSelectie(null); }}>
        {t("viz.vedere_2d")}
      </button>
      <button className={[machetaSit ? "on" : "",
                          machetaVine ? "se-genereaza" : "",
                          stareMacheta === "la_rand" ? "la-rand" : ""].join(" ").trim()}
              title={t(machetaVine ? "viz.sit_macheta_gen" : "viz.sit_macheta_titlu")}
              onClick={() => { setMachetaSit(true); setSelectie(null); }}>
        {t("viz.vedere_3d")}
      </button>
    </div>
  );
  /** VEDEREA ÎN COLȚUL DIN DREAPTA-JOS, pe toate trei vizualizările (Stefan,
   *  23.09.2026: «move the 2d/3d option on the bottom right for all view»).
   *  Meniul din stânga spune CE se vede — corpul, cota, straturile —, colțul
   *  spune CUM: desen sau volum. Același loc pe situație, arhitectură și
   *  structură (`structure/Studio`), ca trecerea dintr-una în alta să nu-l
   *  mute. */
  const comutatorVedere = selectorMod || selectorModSit;

  /** Straturile planșei de situație, ca pe arhitectură: comutatoare cu nume.
   *
   *  «Așezare» aprinde cotele pe care le decide amplasarea; cele ale
   *  CONTURULUI — laturile lotului, colțurile numerotate — sunt ale planșei și
   *  rămân aprinse oricum. Zona interzisă pune aceeași întrebare ca ele — «se
   *  respectă ce scrie în certificat?» — dar răspunde cu o suprafață: dacă un
   *  corp calcă hașura, se vede fără să citești nimic. Stinse amândouă la
   *  pornire: sunt verificări pe care le ceri, nu straturi ale planșei.
   *
   *  Pe machetă, în locul lor, cele două PANOURI: regulile fațadei și însorirea
   *  fațadelor, stinse și ele până le ceri. */
  const [retrageriSit, setRetrageriSit] = useState(false);
  /** Ora ca «13:05» — pe riglă și, cu rigla închisă, pe butonul ei din meniu. */
  const hhmm = (min: number) => {
    const h = Math.floor(min / 60) % 24, m = Math.round(min % 60);
    return `${h < 10 ? "0" : ""}${h}:${m < 10 ? "0" : ""}${m}`;
  };
  const hhmmSoare = hhmm(soarele.minut);
  /** Un singur comutator al orașului pentru plan și machetă: harta e aceeași,
   *  iar trecerea dintr-o vedere în alta n-o stinge. Nota = dalele aduse, cât
   *  serverul încă le aduce. */
  const comutatorOras = comutator("oras", araOras, () => setAraOras((v) => !v),
    "oras", t("viz.arata_oras"), t("viz.arata_oras_titlu"),
    orasul.harta && !orasul.harta.complet
      ? `${orasul.harta.dale.gata} / ${orasul.harta.dale.total}`
      : undefined);
  const selectorAfisareSit = vedeSituatia && !!situatie && (!machetaSit
    ? grup(t("viz.grup.afisare"), (
      <div className="meniu-comutatoare">
        {comutator("cote", coteSit, () => setCoteSit((v) => !v), "cote",
                   t("viz.cote_asezare"), t("viz.cote_asezare_titlu"))}
        {comutator("retrageri", retrageriSit, () => setRetrageriSit((v) => !v),
                   "interzis", t("viz.zona_interzisa"), t("viz.zona_interzisa_titlu"))}
        {comutatorOras}
      </div>
    ))
    : grup(t("viz.grup.afisare"), (
      <div className="meniu-comutatoare">
        {!compact && comutator("fatada", araFatada, () => setAraFatada((v) => !v),
                               "fatada", t("viz.arata_fatada"))}
        {/* Verdictul se citește și cu panoul închis: câte fațade sunt sub
            prag, din câte. */}
        {vedeSituatia && schitaVolumetrie && comutator("schita", araSchita, () => setAraSchita((v) => !v),
                   "sit", t("viz.arata_schita"), t("viz.arata_schita_titlu"))}
        {comutator("insorire", araInsorire, () => setAraInsorire((v) => !v),
                   "soare", t("viz.arata_insorire"), undefined,
                   insorirea?.fatade.length
                     ? `${insorirea.neconforme} / ${insorirea.fatade.length}`
                     : undefined)}
        {comutatorOras}
        {/* Ora de pe riglă se citește și cu rigla închisă. */}
        {comutator("lumina", araSoare, () => setAraSoare((v) => !v),
                   "lumina", t("viz.arata_soare"), t("viz.arata_soare_titlu"),
                   hhmmSoare)}
      </div>
    )));

  /** De unde se privește macheta. Apare doar când e ceva de privit. */
  //  O listă, ca corpul și cota: patru puncte de vedere nu încap ca pastile
  //  în lățimea meniului.
  /** Etajul tăiat al machetei: «Tot» și câte un buton pe etaj. */
  const selectorEtaj = vedeSituatia && machetaSit && etajeMacheta.length > 0 && grup(t("viz.grup.etaj"), (
    <SelectorEtaj etaje={etajeMacheta} ales={etajAles} laAlegere={setEtajMacheta} />
  ));

  const selectorCamera = vedeSituatia && machetaSit && grup(t("viz.grup.camera"), (
    <select className="inp meniu-select" value={vedereaSit}
            onChange={(e) => setVedereaSit(e.target.value as typeof vedereaSit)}>
      {([["ansamblu", "viz.cam.ansamblu"], ["strada", "viz.cam.strada"],
         ["curte", "viz.cam.curte"], ["pasare", "viz.cam.pasare"]] as const)
        .map(([k, n]) => (
        <option key={k} value={k}>{t(n)}</option>
      ))}
    </select>
  ));

  /** Rigla soarelui: minutul din zi, anotimpul, și cifrele care ies din ele.
   *
   *  DOUĂ RÂNDURI, fiindcă pune două întrebări de feluri diferite: CÂND în zi e
   *  o mărime continuă (riglă), CÂND în an e o alegere dintre patru (butoane).
   *  Puse pe același rând, anotimpul arăta ca o etichetă a riglei.
   *
   *  Amiaza e starea de pornire (v. `ceas`), iar rigla stă ascunsă până o ceri
   *  din meniu (`araSoare`). Butonul «amiază» există ca să te poți ÎNTOARCE la
   *  ea după ce ai mutat soarele. */
  // Numele lunii, din catalog: pe rigla soarelui se scrie «21 decembrie», iar
  // luna e text de interfață, nu o etichetă de cod.
  const numeLunii = (l: number) => t(`viz.luna.${l}` as Cheie);
  const baraSoare = vedeSituatia && machetaSit && araSoare && (
    <div className="card bara-soare">
      <div className="rand-soare">
        <span className="grup-vederi" style={{ flexDirection: "column",
                                               alignItems: "flex-start", gap: 2 }}>
          <span className="lbl">{t("viz.soare.titlu")}</span>
          <b className="ora-soare">{hhmm(soarele.minut)}</b>
        </span>
        <span className="rigla-soare">
          <input type="range" min={0} max={1435} step={5} value={soarele.minut}
                 aria-label={t("viz.soare.ora_din_zi")}
                 onChange={(ev) => setCeas((c) => ({ ...c, minut: +ev.target.value }))} />
          <span className="lbl capete">
            <span>{soarele.rasarit == null ? "—" : hhmm(soarele.rasarit * 60)}</span>
            <span className="mijloc">{ceas.zi} {numeLunii(ceas.luna)}</span>
            <span>{soarele.apus == null ? "—" : hhmm(soarele.apus * 60)}</span>
          </span>
        </span>
        <button className="btn mic"
                onClick={() => { setCeas({ ...acumLa(), minut: null }); setAnotimp("azi"); }}>
          {t("viz.soare.amiaza")}
        </button>
      </div>
      <div className="rand-soare">
        <span className="lbl">{t("viz.soare.anotimp")}</span>
        <div className="tabs niveluri mic">
          {([["azi", "azi", 0, 0], ["mar", "21 mar", 3, 21],
             ["iun", "21 iun", 6, 21], ["dec", "21 dec", 12, 21]] as const)
            .map(([k, n, l, z]) => (
              <button key={k} className={anotimp === k ? "on" : ""}
                      onClick={() => {
                        setAnotimp(k);
                        // Ora rămâne unde a pus-o omul: cine se uită la 15:00 și
                        // schimbă anotimpul vrea aceeași oră în decembrie, nu
                        // amiaza de altundeva. Neatinsă, rigla rămâne pe amiază
                        // — a zilei noi (`minut: null` trece mai departe).
                        setCeas((c) => (k === "azi"
                          ? { ...acumLa(), minut: c.minut }
                          : { ...c, an: acumLa().an, luna: l, zi: z }));
                      }}>{n}</button>
            ))}
        </div>
        <span className="cifre-soare">
          <span><i className="lbl">{t("viz.soare.azimut")}</i>
            <b>{Math.round(soarele.az)}° {cardinal(soarele.az)}</b></span>
          <span><i className="lbl">{t("viz.soare.inaltime")}</i>
            <b>{numar(soarele.alt, { maximumFractionDigits: 1 })}°</b></span>
          <span><i className="lbl">{t("viz.soare.umbra")}</i>
            <b>{soarele.alt > 1 && scenaSituatie?.corpuri.length
              ? `${Math.round(Math.max(...scenaSituatie.corpuri.map((c) => c.h))
                              / Math.tan((soarele.alt * Math.PI) / 180))} m`
              : "—"}</b></span>
        </span>
      </div>
    </div>
  );

  /** Verdictul de însorire — al FAȚADELOR PROPUSE, nu al vecinilor.
   *
   *  Scris pe față, fiindcă diferența contează: documentul canonic descrie
   *  parcela, nu cvartalul, deci despre ce umbrim la vecini nu se poate spune
   *  nimic din datele astea. Un panou care ar zice doar «însorire ✓» ar fi citit
   *  ca răspuns la amândouă întrebările. */
  const panouInsorire = vedeSituatia && machetaSit && araInsorire && insorirea && (
    <div className="card panou-insorire">
      <div className="cap-insorire">
        <b>{t("viz.ins.titlu")}</b>
        <span className="lbl">{t("viz.ins.norma")}</span>
      </div>
      {locul.sursa === "implicit" && (
        <p className="note amb">{t("viz.ins.fara_coordonate")}</p>
      )}
      {insorirea.fatade.length === 0
        ? <p className="note">{t("viz.ins.fara_corpuri")}</p>
        : (
          <>
            <ul className="lista-insorire">
              {[...insorirea.fatade].sort((a, b) => a.ore - b.ore).slice(0, 6).map((f) => (
                <li key={`${f.corp}-${f.latura}`}>
                  <span>{f.corp} · {cardinal(f.azimut)}</span>
                  <b>{f.ore.toFixed(1)} h</b>
                  <i className={f.conform ? "ok" : "bad"}>
                    {t(f.conform ? "viz.ins.conform" : "viz.ins.neconform")}</i>
                </li>
              ))}
            </ul>
            <p className="lbl subsol-insorire">
              {(() => {
                // DE UNDE VINE FAȚADA, scris pe față. Golurile nu sunt ale
                // planului de situație — disciplina lui nu are `Window` — ci ale
                // arhitecturii înlănțuite pe el. Când aceea lipsește, volumele
                // rămân fără goluri, iar asta trebuie să se citească: altfel o
                // clădire oarbă arată ca o alegere de reprezentare.
                const n = (scenaSituatie?.fatade ?? [])
                  .reduce((t, f) => t + f.goluri.length, 0);
                if (arhSeIncarca) return t("viz.ins.arh_se_incarca");
                if (!docArh) return t("viz.ins.fara_arh");
                // O arhitectură care încă macină se spune ORICUM, cu goluri
                // sau fără: ce e pe ecran e o etapă din ea, nu rezultatul, iar
                // o regenerare poate scoate cu totul altceva.
                const etapa = etapaJobului(rulareaArh);
                if (rulareaArh?.kind === "facade") {
                  return t("viz.ins.fatada_in_lucru", { stadiu: etapa ?? "…" })
                    + (n ? tn("viz.ins.ferestre#n", n) : "");
                }
                if (arhInLucru || rulareaArh) {
                  return t("viz.ins.arh_in_lucru")
                    + (etapa ? `${etapa} · ` : "")
                    + (n ? tn("viz.ins.ferestre#n", n) : "");
                }
                if (!n) return t("viz.ins.arh_fara_goluri");
                return tn("viz.ins.ferestre#n", n);
              })()}
              {insorirea.neconforme
                ? t("viz.ins.sub_prag", { n: insorirea.neconforme,
                                          total: insorirea.fatade.length })
                : t("viz.ins.toate_trec", { total: insorirea.fatade.length })}
              {t("viz.ins.masurare")}
            </p>
          </>
        )}
    </div>
  );

  /** Zi / noapte, DOAR pe planșa de linie.
   *
   *  Nu e tema interfeței: e tușul desenului. Aceeași planșă, cu cerneala și
   *  hârtia schimbate între ele — negru pe alb, cum se tipărește, sau alb pe
   *  negru, cum se lucrează în CAD. Butonul apare numai pe «Plan», fiindcă
   *  „2D" e o reprezentare cu tonuri: întoarsă pe negru n-ar mai fi ea. */
  const selectorTus = vedere === "plan" && !vedeSituatia && arhActiv
    && grup(t("viz.grup.tus"), (
    <div className="tabs niveluri">
      <button className={!noapte ? "on" : ""}
              title={t("viz.tus_zi_titlu")}
              onClick={() => setNoapte(false)}>{t("viz.tus_zi")}</button>
      <button className={noapte ? "on" : ""}
              title={t("viz.tus_noapte_titlu")}
              onClick={() => setNoapte(true)}>{t("viz.tus_noapte")}</button>
    </div>
  ));

  /** Cifrele planului: pe ETAJ, pe CLĂDIRE și pe ANSAMBLU — apartamente, aria
   *  utilă și aria construită.
   *
   *  Stau la capătul meniului din stânga, fiindcă răspund la «ce mă uit» — nu în
   *  cheia de legendă, care răspunde la «ce înseamnă cifrele de pe desen». Trei
   *  grupe una sub alta, cu numele întregi pe rând (nu un tabel cu «S.u.» și
   *  «S.c.»): în 212 px o abreviere cere o legendă, iar legenda ar fi un al
   *  doilea loc de citit. Ales pe planșa din Claude Design, varianta B.
   *
   *  Etajul e ce se vede: cu «toate corpurile» se adună peste toate planșeele
   *  desenate. Pe clădire și pe ansamblu aria construită e desfășurată
   *  supraterană (v. `cifreArhitectura`). Un rând lipsește dacă documentul nu-l
   *  dă — un «0 apartamente» pe un subsol de parcaj ar fi adevărat și inutil. */
  const cifre = !vedeSituatia && arhActiv && (() => {
    const c = cifreArhitectura(model?.niveluri ?? [], planseeActive,
                               corpActiv === TOATE_CORPURILE ? null : corpActiv,
                               model?.registru);
    const cota = arhActiv.cota;
    const m2 = (v: number) => <>{numar(v, { maximumFractionDigits: 0 })} m²</>;
    const grupa = (cheie: string, titlu: React.ReactNode, sub: React.ReactNode,
                   g: CifreGrup, desfasurata: boolean) => {
      const randuri: [string, React.ReactNode, boolean?][] = [];
      if (g.apartamente > 0) randuri.push([t("viz.cifre.apartamente_rand"),
                                           numar(g.apartamente)]);
      // Sub apartamente, câte are fiecare număr de camere — rânduri retrase.
      for (const [k, n] of g.peCamere) {
        randuri.push([tn("viz.cifre.pe_camere#n", k), numar(n), true]);
      }
      // Aria utilă, cu balcoanele și logiile SEPARAT: titlul fără cifră, iar
      // dedesubt ce e închis și ce e deschis, fiecare cu aria lui — cum se
      // scrie în tabelul de suprafețe al unui proiect.
      if (g.utila > 0 || g.balcoane > 0) {
        randuri.push([t("viz.cifre.arie_utila_rand"), null]);
        randuri.push([t("viz.cifre.utila_fara_balcoane"), m2(g.utila), true]);
        if (g.balcoane > 0) randuri.push([t("viz.cifre.utila_balcoane"),
                                          m2(g.balcoane), true]);
        if (g.terase > 0) randuri.push([t("viz.cifre.utila_terase"),
                                        m2(g.terase), true]);
      }
      if (g.construita > 0) randuri.push([t(desfasurata
        ? "viz.cifre.arie_construita_desf" : "viz.cifre.arie_construita"),
        m2(g.construita)]);
      if (!randuri.length) return null;
      return (
        <div key={cheie} className="cifre-grup">
          <div className="cifre-cap"><span>{titlu}</span><small>{sub}</small></div>
          {randuri.map(([n, v, sub]) => (
            <div key={n} className={`cifre-rand${sub ? " sub" : ""}`}>
              <span>{n}</span><b>{v}</b>
            </div>
          ))}
        </div>
      );
    };
    // Etajul poartă numele blocului NUMAI când blocul e ales; la «toate
    // corpurile» e etajul tuturor, chiar dacă la cota asta stă unul singur.
    // Eticheta planșeului poartă deja codul când la cotă stau mai multe corpuri
    // («Parter · C5», v. `toStoreyPlans`) — se scoate, și se pune la loc doar
    // când blocul e chiar ales.
    const sufix = arhActiv.cod ? ` · ${arhActiv.cod}` : "";
    const nivel = sufix && arhActiv.label.endsWith(sufix)
      ? arhActiv.label.slice(0, -sufix.length) : arhActiv.label;
    const numeEtaj = corpActiv === TOATE_CORPURILE
      ? t("viz.cifre.etaj_toate", { nivel }) : `${nivel}${sufix}`;
    const cotaTxt = cota != null ? <>{t("viz.cifre.cota")}{" "}
      {cota >= 0 ? "+" : "−"}{numar(Math.abs(cota), { minimumFractionDigits: 2,
                                                   maximumFractionDigits: 2 })}</> : null;
    const grupe = [
      grupa("etaj", numeEtaj, cotaTxt, c.etaj, false),
      c.cladire && grupa("cladire", t("viz.cifre.cladirea", { cod: c.cladire.cod }),
        tn("viz.cifre.niveluri#n", c.cladire.niveluri), c.cladire, true),
      c.total && grupa("total", t("viz.cifre.ansamblu"),
        tn("viz.cifre.cladiri#n", c.total.cladiri), c.total, true),
    ].filter(Boolean);
    if (!grupe.length) return null;
    // Sub un buton care se desface, «Suprafețe»: meniul are deja vederile,
    // corpul, nivelul și filtrele, iar trei grupe de cifre deschise mereu îl
    // lungeau cât ecranul. Închis implicit; deschiderea se ține cât stă omul
    // pe pagină, de la un nivel la altul.
    return (
      <div className="cifre-plan">
        <button className={`cifre-comutator${suprafeteDeschise ? " on" : ""}`}
                aria-expanded={suprafeteDeschise}
                onClick={() => setSuprafeteDeschise((v) => !v)}>
          <span>{t("viz.cifre.suprafete")}</span>
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {suprafeteDeschise && <div className="cifre-grupe">{grupe}</div>}
      </div>
    );
  })();

  /** SUPRAFEȚE GENERALE — situația generală a clădirii alese, altfel a
   *  ansamblului (Cătălin, 6.10.2026): SU fără terase, SU terase, SU spații
   *  comune, ST construit și cele două procente. Un al doilea buton care se
   *  desface, sub «Suprafețe», închis implicit. Definițiile — ce intră în
   *  fiecare rând — sunt la `situatieGenerala`. */
  const cifreGenerale = !vedeSituatia && arhActiv && (() => {
    const g = cifreArhitectura(model?.niveluri ?? [], planseeActive,
                               corpActiv === TOATE_CORPURILE ? null : corpActiv,
                               model?.registru).generala;
    if (!g || g.stConstruit <= 0) return null;
    const m2 = (v: number) => <>{numar(v, { maximumFractionDigits: 0 })} m²</>;
    const pct = (v: number | null) => (v == null ? "—"
      : <>{numar(v, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %</>);
    const randuri: [string, React.ReactNode][] = [
      [t("viz.gen.su_fara_terase"), m2(g.suFaraTerase)],
      [t("viz.gen.su_terase"), m2(g.suTerase)],
      [t("viz.gen.su_comune"), m2(g.suComune)],
      [t("viz.gen.st_construit"), m2(g.stConstruit)],
      [t("viz.gen.pct_su_st"), pct(g.pctSuSt)],
      [t("viz.gen.pct_su_st_fara_comune"), pct(g.pctSuStFaraComune)],
    ];
    return (
      <div className="cifre-plan">
        <button className={`cifre-comutator${generaleDeschise ? " on" : ""}`}
                aria-expanded={generaleDeschise}
                onClick={() => setGeneraleDeschise((v) => !v)}>
          <span>{t("viz.gen.titlu")}</span>
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {generaleDeschise && (
          <div className="cifre-grupe">
            <div className="cifre-grup">
              <div className="cifre-cap">
                <span>{t("viz.gen.situatie")}</span>
                <small>{g.cod ? t("viz.cifre.cladirea", { cod: g.cod })
                              : `${t("viz.cifre.ansamblu")} · ${tn("viz.cifre.cladiri#n", g.cladiri)}`}</small>
              </div>
              {randuri.map(([n, v]) => (
                <div key={n} className="cifre-rand"><span>{n}</span><b>{v}</b></div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  })();

  /** Obiectele documentului pe care niciun randor nu le desenează, gata de pus
   *  pe pânză — cele care au ce căuta pe planșeele astea. V. `fantomeDePus`. */
  const fantome = useMemo(() => (
    vedeStrat("nedesenate") && !macheta
      ? fantomeDePus(model?.inventar.fantome ?? [],
                     planseeActive.map((n) => n.id))
      : []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [model, planseeActive, ascunse, macheta]);

  // FILTRELE PLANULUI DE ARHITECTURĂ: trei, și atât — cotele, numele
  // camerelor și colorarea apartamentelor, aceleași ca pe masa unui corp (v.
  // `ComutatoareMasa`, `fel="nivel"`). Stefan, 23.09.2026: «vreau totuși
  // câteva filtre, cel de cote, etichete, și cel de colorarea apartamentelor,
  // atât». Până atunci aici stătea panoul de straturi, cu paisprezece bife.
  const comutaStrat = (cheie: Strat) => setAscunse((v) => {
    const nou = new Set(v);
    if (!nou.delete(cheie)) nou.add(cheie);
    return nou;
  });
  // Fiecare cu NUMELE scris lângă icoană (Stefan, 23.09.2026: «add text for
  // the 3 toggle options, cote, camere etc»): trei glife de linie una lângă
  // alta nu spuneau care e care fără să treci cu mouse-ul peste ele.
  // Ce rampe TRECE prin nivelul privit — de aici se știe care din cele două
  // butoane ale rampelor au ce aprinde. Rampa de acces stă pe -1 (documentul o
  // pune doar acolo); cea dintre niveluri se desenează pe -1 și pe -2.
  const rampeAccesPeNivel = planuriActive.some((pl) => (pl.accessRamps?.length ?? 0) > 0);
  const rampeInteriorPeNivel = planuriActive.some((pl) =>
    (pl.rampsInt?.length ?? 0) > 0 || rampaValida(pl) != null);
  // Apartamentele, la fel: un subsol n-are niciunul, iar un buton care nu
  // aprinde nimic pe nivelul privit doar încurcă.
  // Din planșeele nivelului, nu din `planuriActive`: aceea e goală când
  // planșa de parcaj e stinsă.
  const apartamentePeNivel = planseeActive.some((n) => (n.flatShapes?.length ?? 0) > 0);
  // Planul de situație SUB planșa de nivel stă tot aici, ca al patrulea
  // comutator: e un strat care se adaugă peste desen, ca celelalte trei.
  // Apare numai când documentul chiar poartă situl — altfel ar promite o
  // suprapunere pe care n-are din ce s-o facă.
  const cuSitSub = !vedeSituatia && !macheta && !!situatie;
  const selectorFiltre = !vedeSituatia && arhActiv && !macheta
    && (vedere === "plan" || cuSitSub)
    && grup(t("viz.grup.afisare"), (
    <div className="meniu-comutatoare">
      {vedere === "plan" && ([["cote", "cote", "editor.ascunde_cote", "editor.arata_cote", "viz.filtru.cote"],
         ["etichete", "etichete", "editor.ascunde_etichete", "editor.arata_etichete",
          "viz.filtru.camere"],
         // Etichetele OBIECTELOR (tâmplăria ALA: «UME1 · prag 15 cm»), altele
         // decât numele camerelor — v. `STRATURI.obiecte` (Oana, 30.09.2026).
         ["obiecte", "etichete", "editor.ascunde_obiecte", "editor.arata_obiecte",
          "viz.filtru.etichete"],
         ["apartamente", "apartamente", "editor.ascunde_apartamente",
          "editor.arata_apartamente", "viz.filtru.apartamente"],
         // Mobilierul desenat în module (`Furniture`, 3.14) — Cătălin,
         // 21.10.2026: «un buton similar cu apartamente, pentru mobilier».
         ["mobilier", "mobilier", "editor.ascunde_mobilier",
          "editor.arata_mobilier", "viz.filtru.mobilier"]] as const)
        .filter(([strat]) => strat !== "apartamente" || apartamentePeNivel)
        .map(([strat, icoana, stinge, aprinde, nume]) => {
        const pornit = vedeStrat(strat);
        return (
          <button key={strat} className={`meniu-comutator${pornit ? " on" : ""}`}
                  aria-pressed={pornit}
                  title={t(pornit ? stinge : aprinde)}
                  onClick={() => comutaStrat(strat)}>
            <Icoana nume={icoana} marime={15} />
            <span>{t(nume)}</span>
          </button>
        );
      })}
      {/* REZISTENȚA LA FOC (ISU): culorile claselor pe pereți și legenda lor.
          Nu în lista de mai sus fiindcă nu e în `ascunse`: implicitul lui e al
          nivelului — v. `isu`. */}
      {vedere === "plan" && arhActiv && comutator("foc", isuActiv, comutaIsu, "pensula",
                   t("viz.filtru.foc"),
                   t(isuActiv ? "editor.ascunde_foc" : "editor.arata_foc"))}
      {/* RAMPELE, PE TRASEU, nu toate deodată. Pe -1 stau una peste alta rampa
          de acces (parter → -1) și cea spre -2 (tiparul S), iar cine se uită
          la locurile de sub ele vrea să stingă DOAR una (Oana, 28.09.2026).
          Fiecare buton apare numai când nivelul privit chiar are rampe pe
          traseul lui: pe -2 rămâne doar «-1 → -2», fiindcă rampa de acces
          nu ajunge acolo. Straturile sunt cele două din catalog
          (`rampe_acces`, `rampe_interior`), deci bifa ține și când schimbi
          nivelul. Aici, nu în «Depanare»: se cer și pe viewerul obișnuit. */}
      {vedere === "plan" && ([["rampe_acces", rampeAccesPeNivel, "viz.filtru.rampe_acces",
                               "strat.rampe_acces"],
         ["rampe_interior", rampeInteriorPeNivel, "viz.filtru.rampe_interior",
          "strat.rampe_interior"]] as const)
        .filter(([, are]) => are)
        .map(([strat, , nume, titlu]) => {
        const pornit = vedeStrat(strat);
        return (
          <button key={strat} className={`meniu-comutator${pornit ? " on" : ""}`}
                  aria-pressed={pornit}
                  title={t(pornit ? "viz.filtru.ascunde" : "viz.filtru.arata",
                           { ce: t(titlu) })}
                  onClick={() => comutaStrat(strat)}>
            <Icoana nume="sageata" marime={15} />
            <span>{t(nume)}</span>
          </button>
        );
      })}
      {/* CĂILE DE EVACUARE ale subsolului: traseul cel mai lung al fiecărei
          ieșiri SAS (`diagnostics.evacuare`), numai pe nivelurile care le au.
          Numărul = câte ieșiri are nivelul. */}
      {vedere === "plan" && traseeEvacuare && comutator("evacuare", araEvacuare,
                   () => setAraEvacuare((v) => !v), "sageata",
                   t("viz.arata_evacuare"), t("viz.arata_evacuare_titlu"),
                   // numărul de IEȘIRI, nu de trasee: o ieșire cu două locuri
                   // la fel de departe are două trasee, dar e tot o ieșire
                   `${new Set(traseeEvacuare.map((x) => x.iesire)).size}`)}
      {/* SEMNELE DE CIRCULAȚIE ale subsolului (`diagnostics.semne_circulatie`): cedează
          la gura aleilor secundare, săgeți pe benzi. Numărul = câte semne are nivelul. */}
      {vedere === "plan" && semneCirculatie && comutator("circulatie", araCirculatie,
                   () => setAraCirculatie((v) => !v), "cedeaza",
                   t("viz.arata_circulatie"), t("viz.arata_circulatie_titlu"),
                   `${semneCirculatie.length}`)}
      {cuSitSub && comutator("sit", sitSubPlan, () => setSitSubPlan((v) => !v),
                             "sit", t("viz.sit_sub_plan"), t("viz.sit_sub_plan_titlu"))}
    </div>
  ));

  // RIGLA, pe orice vizualizator plin, pe desen (nu pe machetă): pe ecranul de
  // încercări în grupul «Depanare», ca până acum, altfel în «Unelte».
  // Comutatorul rampelor a stat aici, unul singur pentru amândouă straturile;
  // acum sunt două, pe traseu, în «Afișare» (v. `selectorFiltre`).
  // Rigla merge și pe vederea situației: ecranul «Circulation Inspector»
  // deschide un rând de plan, cu filmul lui de la sol.
  const riglaDisponibila = (depanare || plin) && !compact;
  const selectorDepanare = riglaDisponibila && !macheta
    && grup(depanare ? t("viz.grup.depanare") : t("viz.grup.unelte"), (
    <div className="meniu-comutatoare">
      <button className={`meniu-comutator${rigla ? " on" : ""}`}
              aria-pressed={rigla}
              title={rigla ? t("viz.rigla_stinge_titlu", { n: masuri.length })
                           : t("viz.rigla_titlu")}
              onClick={() => (rigla ? stingeRigla() : setRigla(true))}>
        <Ic n="ruler" s={15} />
        <span>{t("viz.rigla")}{masuri.length ? ` · ${masuri.length}` : ""}</span>
      </button>
    </div>
  ));

  // În cardul mic (boardul de soluții) barele n-au loc: acolo rămân dropdownuri
  // — dar tot DOUĂ, fiindcă ce se întreabă e același lucru.
  const selectorCompact = !!model?.cote.length && !vedeSituatia && (
    <select value={nivelActiv ?? ""} style={{ width: "auto" }}
            onChange={(e) => { setNivelAles(Number(e.target.value));
                               setSelectie(null); }}>
      {model.cote.map((c) => (
        <option key={c.level} value={c.level}>{c.eticheta}</option>
      ))}
    </select>
  );

  /** Scara grafică, în PIXELI, nu în metri.
   *
   *  În desen era o bară de 20 m: corectă la o singură mărire și greșită la
   *  toate celelalte, fiindcă se micșora odată cu planul. Aici e invers — bara
   *  are o lungime constantă pe ecran și SCRIE câți metri sunt, ceea ce e chiar
   *  întrebarea la care răspunde o scară grafică.
   *
   *  Lungimea se alege dintr-o serie 1-2-5, cea mai mare care încape în ~150 px:
   *  altfel ar scrie „13,7 m", un număr pe care nimeni nu-l citește de pe o
   *  riglă. */
  const scaraGrafica = view && (() => {
    const TINTA = 150;
    const trepte = [0.5, 1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000];
    let m = trepte[0];
    for (const t of trepte) if (t * view.k <= TINTA) m = t;
    const px = m * view.k, cel = px / 4;
    // Marginile nu sunt decor: etichetele sunt centrate pe capete, deci „0" iese
    // pe stânga dacă bara începe la zero, iar unitatea se suprapune peste ultima
    // cifră dacă e scrisă separat. Unitatea intră ÎN eticheta din dreapta.
    const M = 12;
    const et = (v: number) => (Number.isInteger(v) ? `${v}` : v.toFixed(1));
    return (
      <svg width={px + 2 * M + 10} height={30} aria-label={`scara: ${m} metri`}>
        {[0, 1, 2, 3].map((i) => (
          <rect key={i} x={M + i * cel} y={2} width={cel} height={7}
            fill={i % 2 ? "#ffffff" : "#1a1a1a"}
            stroke="#1a1a1a" strokeWidth={0.8} />
        ))}
        {[0, m / 2, m].map((v, i) => (
          <text key={i} x={M + (i * px) / 2} y={22} fontSize={10}
            fontFamily="system-ui, sans-serif" textAnchor="middle" fill="#737373">
            {i === 2 ? `${et(v)} m` : et(v)}
          </text>
        ))}
      </svg>
    );
  })();

  /** Bilanțul spațiilor verzi. Cifrele sunt ale documentului — suprafețe
   *  măsurate, nu citite de pe desen — deci nu se schimbă cu mărirea sau cu
   *  rotirea, și n-au ce căuta în pânză. */
  const legenda = vedeSituatia && siteActiv && (
    // Pe ecran, cardul temei (doar cifre); tabelul de planșă (`GreenBalance`)
    // rămâne al exportului — v. `IndicatoriCard`.
    <IndicatoriCard areas={siteActiv.areas ?? {}} indicatori={siteActiv.indicatori} />
  );

  /** Legenda ISU: tabelul claselor de rezistență la foc, betonul ALA și
   *  emblemele ușilor — pe subsol, cât stratul ISU e aprins. Tot tabelul, nu
   *  doar clasele de pe nivel (Oana, 20.10.2026); cutia se strânge din butonul
   *  ei, ca până acum. */
  const legendaFoc = !vedeSituatia && !macheta && !!arhActiv?.parcare && isuActiv
    && <LegendaFoc />;

  /** Scara și legenda, într-o singură cutie, cu scara deasupra.
   *
   *  Strânsă, rămâne DOAR pictograma — trei rânduri de eșantion, în chiar
   *  culorile pe care le explică. Un plic sau un „i" ar fi cerut un cuvânt
   *  lângă el ca să se înțeleagă ce deschide; o legendă în miniatură nu. */
  const cutieLegenda = (legenda || scaraGrafica || legendaFoc) && (
    legendaDeschisa ? (
      // `maxHeight` + derulare: legenda își ia acum înălțimea din conținut, dar
      // pe o fereastră scundă — sau cu încă vreo două rânduri adăugate cândva —
      // ar crește peste marginea de sus a ecranului. Plafonul o ține în pagină,
      // iar ce nu încape se derulează, în loc să dispară tăiat.
      <div className="card legenda-plutitoare"
           style={{ position: "relative", padding: "10px 12px",
                                     display: "flex", flexDirection: "column",
                                     gap: 6, alignItems: "flex-start",
                                     maxHeight: "calc(100vh - 220px)",
                                     overflowY: "auto", overscrollBehavior: "contain" }}>
        {/* Chrome-ul e scris pe loc, nu moștenit: regulile de buton ale temei
            sunt legate de `.seg` și `.tabs`, iar butonul ăsta nu e în niciuna,
            deci ar fi luat implicitul browserului — altă bordură, altă înălțime,
            pe fiecare platformă alta. */}
        <button onClick={() => setLegendaDeschisa(false)}
          title={t("viz.legenda_strange")}
          aria-label={t("viz.legenda_strange_aria")}
          style={{ position: "absolute", right: 6, top: 6, width: 20, height: 20,
                   padding: 0, display: "inline-flex", alignItems: "center",
                   justifyContent: "center", lineHeight: 1, cursor: "pointer",
                   background: "none", border: "none", color: "var(--ui-muted)" }}>
          <svg width={10} height={10} viewBox="0 0 10 10" aria-hidden="true">
            <path d="M1 5h8" stroke="currentColor" strokeWidth={1.6}
              strokeLinecap="round" />
          </svg>
        </button>
        {scaraGrafica}
        {legenda}
        {legendaFoc}
      </div>
    ) : (
      <div className="card" style={{ padding: 0, lineHeight: 0 }}>
      <button onClick={() => setLegendaDeschisa(true)}
        title={t("viz.legenda_arata")}
        aria-label={t("viz.legenda_arata_aria")}
        style={{ padding: 7, display: "inline-flex", lineHeight: 0,
                 cursor: "pointer", background: "none", border: "none" }}>
        <svg width={26} height={26} viewBox="0 0 26 26" aria-hidden="true">
          {[[5, A01.deep], [11.5, A01.slab], [18, A01.road]].map(
            ([y, c]) => (
              <g key={y as number}>
                <rect x={3} y={y as number} width={6} height={5} rx={1}
                  fill={c as string} stroke="#1a1a1a" strokeWidth={0.7} />
                <rect x={11.5} y={(y as number) + 1.6} width={11} height={1.8}
                  rx={0.9} fill="#737373" />
              </g>
            ))}
        </svg>
      </button>
      </div>
    )
  );

  /** Busola, ca obiect al interfeței: lângă desen, nu în el.
   *
   *  Se rotește cu desenul, fiindcă asta ARATĂ — unde a ajuns nordul după ce ai
   *  învârtit planul. Apare pe orice document GEOREFERENȚIAT, nu doar pe planul
   *  de situație: după `northUp` planurile de nivel sunt în același cadru, deci
   *  nordul e la fel de cunoscut pe ele. Desenul pornește acum mereu cu nordul
   *  în sus (v. `incadreaza`), deci busola nu mai are de mărturisit o întoarcere
   *  pe care n-a cerut-o nimeni — dar rămâne, fiindcă rotirea din `q`/`e` se
   *  păstrează între planșe, iar atunci ea e singurul lucru care spune încotro
   *  s-a ajuns. */
  const busola = georeferenced && (
    <div className="card" style={{ padding: "8px 10px", display: "flex",
                                   flexDirection: "column", alignItems: "center",
                                   gap: 2, pointerEvents: "none" }}>
      {/* SQUARE viewBox, and no big "N".
          The rose turns, so whatever it contains sweeps a circle — the box has
          to hold that circle, not the upright glyph. The title "N" sits 1.75 r
          above the star, so it swept a circle half again as wide as the box and
          was clipped on the way round. It also only repeats what the star's own
          "n" says, so in a widget this size it goes. */}
      <svg width={58} height={58} viewBox="-18 -18 36 36" aria-label="nord">
        <g transform={`rotate(${rot})`}>
          <CompassRose x={0} y={0} r={11} title={false} />
        </g>
      </svg>
      {/* Fără etichetă dedesubt (Stefan, 23.09.2026: «remove the label Nord
          sus»): steaua arată singură încotro e nordul. */}
    </div>
  );

  const panza = cadru && dim && view && (
    /* `masoara`: cu rigla aprinsă pânza trece pe SĂGEATA obișnuită, fiindcă
       vârful ei e chiar punctul măsurat — v. `.panza.masoara` în `app.css`. */
    <svg ref={svgRef} className={`panza${rigla ? " masoara" : ""}`}
         width={dim.w} height={dim.h}
         style={planNoapte
           ? { background: "#0e1424",
               ["--tus" as string]: "#e6eaef",
               ["--hartie" as string]: "#0e1424" } as React.CSSProperties
           : undefined}
         onPointerDown={apasa} onPointerMove={mișcă} onPointerUp={ridică}
         onDoubleClick={dubluClic}
         onPointerLeave={() => { tragere.current = null; }}>
      <Defs />
      <g transform={`translate(${view.tx} ${view.ty}) scale(${view.k})`
                    + (rot && pivot ? ` rotate(${rot} ${pivot.x} ${pivot.y})` : "")}>
        {/* ORAȘUL DIN JUR, sub planul lotului: hârtia lotului îl acoperă la
            limită. Ca pe machetă, nu ca pe planșă — exportul nu-l poartă. */}
        {vedeSituatia && situatie && orasul.oras && <StratOras2D oras={orasul.oras} />}
        {vedeSituatia && situatie && (
          /* `pxPerM` e SCARA CURENTĂ, nu o constantă: desenul e în metri, iar
             `view.k` e exact câți pixeli iese un metru. Fără el componenta lua
             implicitul (40) și toate pragurile ei de detaliu treceau mereu —
             adică numerele locurilor și lanțurile de cote se desenau și la
             încadrarea pe tot lotul, unde sunt doar un păienjeniș. */
          /* `showCompass={false}`: pe ecran busola e un obiect al INTERFEȚEI,
             lângă desen, ca să nu se micșoreze cu el și ca să se poată roti
             singură. Pe planșa exportată rămâne în desen, unde îi e locul. */
          <SitePlanView site={situatieFilm!.site} lot={situatie.lot} interactive
                        showCompass={false} showSheet={false} viewTurn={rot}
                        arataCote={coteSit} arataRetrageri={retrageriSit}
                        pxPerM={kDetaliu} selected={selectie} />
        )}
        {/* ZONELE ADMISIBILE PENTRU RAMPE NU MAI STAU AICI (Oana, 14.09.2026).
            Erau desenate și pe planul de situație, și pe planșa de parcaj. Dar
            întrebarea pe care o explică — «de ce a ieșit gura rampei fix
            acolo» — e a SUBSOLULUI: pe situație, unde nu e nicio rampă de
            văzut, hașura acoperea lotul cu un strat pe care nimeni nu avea ce
            să-l compare. A rămas o singură casă, pe arhitectură, și acolo
            stinsă implicit — v. `ascunse`. */}
        {/* SITUL CA FUNDAL, sub tot ce urmează.

            `pxPerM` MIC ÎNADINS, nu scara curentă. Geometria e în metri, deci
            nu se schimbă cu el — singurul lucru pe care îl comandă sunt
            pragurile de detaliu ale planșei de situație. La scara unui etaj
            (20-30 px/m) ele trec toate, iar sub apartamente ar veni mașini,
            numere de loc, indicatoare și lanțuri de cote: un păienjeniș prin
            care nu se mai vede planul. La 2 px/m rămân drumurile, verdele,
            trotuarele și amprentele corpurilor — adică exact contextul cerut,
            încotro dă fiecare fațadă.

            `pointerEvents="none"` și `interactive` stins: e un fundal, iar un
            clic trebuie să aleagă apartamentul de deasupra, nu aleea de sub el. */}
        {/* SPĂLAT CU HÂRTIE DEASUPRA, nu `opacity` pe grup (Stefan, 23.09.2026:
            «the overlay … is flickering»). Opacitatea de grup cere browserului
            să redeseneze tot situl într-un strat separat la FIECARE cadru de
            panoramare și zoom — sute de suprafețe cu pattern-uri decupate pe
            lot —, iar când nu ține pasul, bucăți din el lipsesc un cadru.
            Un dreptunghi translucid peste sit dă același ton fără stratul
            separat. */}
        {!vedeSituatia && sitSubPlan && situatie && (
          <g pointerEvents="none">
            <SitePlanView site={situatie.site} lot={situatie.lot}
                          showCompass={false} showSheet={false} viewTurn={rot}
                          arataCote={false} pxPerM={2} />
            <rect x={-1e4} y={-1e4} width={2e4} height={2e4}
                  fill={planNoapte ? "#0e1424" : "var(--paper)"} fillOpacity={0.62} />
          </g>
        )}
        {/* PLANȘA DE PARCAJ ÎNTÂI, vederea de nivel DEASUPRA. Era invers, și pe
            un garaj se vedeau doar locurile: platelajul și aleile sunt umpleri
            opace, iar peste ele venea tot ce desenase `StoreyView` dedesubt —
            pereții de beton coborâți din etaje, stâlpii, nucleele — adică exact
            elementele blocante după care s-au așezat locurile. Acum ele stau
            peste alei, iar `StoreyView` nu-și mai pune hârtia când are planșa
            de parcaj sub el. */}
        {!vedeSituatia && planuriActive.map((pl) => {
          // Conturul pe care `PlanView` întinde platelajul e al STRUCTURII de
          // sub el, deci se ia per planșeu, nu o dată pentru toate.
          const nivel = planseeActive.find((n) => n.id === pl.storey);
          const contur = (nivel?.outline.length ?? 0) >= 3
            ? (nivel!.outline as Pt[]) : (conturStructurii ?? cadru.lot);
          if (!contur) return null;
          return (
            <PlanView key={pl.storey} boundary={contur as Pt[]}
                      /* Fără stâlpi: sunt obiecte `Column` în document și îi
                         desenează stratul «Structură desenată». */
                      plan={filmAprins ? { ...pl, spaces: [], lanes: [] }
                            : edParcaj.activ && edParcaj.storey === pl.storey
                              ? { ...pl, spaces: edParcaj.locuri, lanes: edParcaj.alei } : pl}
                      rooms={pl.rooms ?? []}
                      rampInt={rampaValida(pl)}
                      level={pl.level}
                      interactive selected={edParcaj.activ ? edParcaj.selectie : selectie}
                      showAccessRamps={vedeStrat("rampe_acces")}
                      showRampInt={vedeStrat("rampe_interior")}
                      /* în editarea parcajului corpul aleilor e mereu desenat:
                         pe el se apasă ca să alegi aleea și să-i tragi capetele */
                      showLaneBodies={vedeStrat("alei") || edParcaj.activ}
                      showNumbers />
          );
        })}
        {/* Câte un desen PER PLANȘEU. Cu un corp ales e unul; cu «toate
            corpurile» sunt toate cele de la cota asta, în cadrul lor comun —
            adică planșa de etaj a ansamblului. */}
        {!vedeSituatia && planseeActive.map((n) => (
          /* `pxPerM={kDetaliu}` — scara CURENTĂ, nu o constantă: hașura
             pardoselii și mobilierul se sting singure când desenul e depărtat.
             Vezi nota de la `SitePlanView`, care a avut exact bug-ul opus.
             Curentă după ce zoom-ul s-a așezat, nu la fiecare cadru al lui. */
          <StoreyView conventie={conventie} key={n.id} plan={n} interactive
                      selected={selectie}
                      ascunse={vedeIsu(n) ? ascunse : ascunseFaraIsu} pxPerM={kDetaliu}
                      faraNumeSiCote={cuAdnotariTronson(n)}
                      dedesubt={dedesubtDe(n)}
                      textInLucru={t("arh.recomp.in_lucru")}
                      evidentiate={evidentiate ?? undefined}
                      previzRecomp={previzRecomp}
                      evacuare={araEvacuare && locuriDesenate}
                      /* fără hârtie peste planșa de parcaj, altfel umplerea
                         ei opacă o acoperă. Peste SIT hârtia rămâne: acoperă
                         doar amprenta nivelului, iar fără ea iarba, asfaltul
                         și banda clădirii din sit se vedeau prin camere. */
                      hartie={!planuriActive.some((pl) => pl.storey === n.id)} />
        ))}
        {/* NUMELE CAMERELOR ȘI COTELE, scrise ca pe tronson — v.
            `AdnotariNivel`. Peste TOATE planșeele, după ele: cotele unui corp
            ies în afara lui și n-au voie să intre sub hârtia vecinului. */}
        {!vedeSituatia && docLocal && planseeActive.filter(cuAdnotariTronson).map((n) => (
          <AdnotariNivel key={`adn-${n.id}`} doc={docAdnotari ?? docLocal} storeyId={n.id}
                         pxPerM={kDetaliu}
                         etichete={vedeStrat("etichete")}
                         cote={vedeStrat("cote")}
                         {...(conventie === "tehnic_noapte"
                              ? { tus: PALETA.tehnic_noapte.tus,
                                  hartie: PALETA.tehnic_noapte.hartie } : {})} />
        ))}
        {/* Stratul de apartamente al solverului — bare, nuclee, contururi de
            corp — ca PLASĂ DE SIGURANȚĂ, nu ca strat obișnuit.
            Pe un nivel cu `Space`-uri nu adaugă nimic ce `StoreyView` nu
            desenează deja din aceleași obiecte (nucleele sunt `Space`-uri
            `scara`/`lift`/`sas`), în schimb aduce convențiile planșei tehnice:
            contur de corp în tuș de 0,34 m, caseta întreruptă a balconului,
            etichetele nucleului în cărămiziu — adică exact semnele după care
            desenul se citea „a CAD". */}
        {!vedeSituatia && etajeActive.map((e, i) => {
          // Se desenează DOAR când planșeul n-are din ce fi desenat altfel. Un
          // document al cărui nivel n-are `Space`-uri (solverul le poate scrie
          // doar ca `Flat`) ar rămâne o pânză goală, iar „mai curat" nu poate să
          // însemne „gol".
          if ((planseeActive[i]?.spaces.length ?? 0) > 0) return null;
          return (
          <ApartmentView key={planseeActive[i]?.id ?? i} level={e} interactive
                         selected={selectie}
                         entrances={siteActiv?.entrances ?? []}
                         /* strat peste plan: fără pereți, camere, uși și
                            etichete redesenate — v. `suprapus` */
                         suprapus ascunse={ascunse} />
          );
        })}
        {/* Ce documentul ține și desenul nu arată. Ultimul strat al planului,
            fiindcă e o întrebare DESPRE el — dar sub adnotarea validatorului,
            care e un verdict și rămâne deasupra. */}
        <Fantome fantome={fantome} />
        {/* Conflictele și fantoma locului nou ale editorului de parcaj. */}
        {!vedeSituatia && edParcaj.strat}
        {vedeSituatia && araSchita && schitaVolumetrie && doc && (
          <SchitaFantoma volume={schitaVolumetrie} la={punctInCadru(doc.crs, origine)} />
        )}
        {/* Semnele stau pe benzi, iar căile de evacuare trec peste benzi: semnele
            dedesubt, ca linia evacuării să se vadă continuă. */}
        {!vedeSituatia && araCirculatie && locuriDesenate && semneCirculatie && doc && (
          <SemneCirculatie semne={semneCirculatie} la={punctInCadru(doc.crs, origine)}
                           pxPerM={kDetaliu} />
        )}
        {!vedeSituatia && araEvacuare && locuriDesenate && traseeEvacuare && doc && (
          <EvacuareTrasee trasee={traseeEvacuare} la={punctInCadru(doc.crs, origine)}
                          alei={planseeActive.flatMap((n) => n.lanes)} />
        )}
        {/* calea locului ales, peste traseele cele mai lungi; și cu stratul stins:
            clicul pe loc e cererea */}
        {!vedeSituatia && !edParcaj.activ && locuriDesenate && rutaLoculuiAles && doc && (
          <EvacuareTrasee trasee={[rutaLoculuiAles]} la={punctInCadru(doc.crs, origine)}
                          alei={planseeActive.flatMap((n) => n.lanes)} ales />
        )}
        {/* roșu: locurile fără rută după recalculare (`fara_ruta`) și cele lăsate de
            generator fără cale de evacuare, marcate în document (§35) */}
        {!vedeSituatia && araEvacuare && locuriDesenate && (
          <g pointerEvents="none">
            {planseeActive.flatMap((n) => n.bays)
              .filter((b) => (b.faraEvacuare || faraRutaEvacuare?.has(b.eid)) && b.poly.length >= 3)
              .map((b) => (
                <polygon key={`fr-${b.eid}`} points={b.poly.map((p) => p.join(",")).join(" ")}
                         fill="rgba(200, 40, 40, .35)" stroke="#c42828" strokeWidth={2}
                         vectorEffect="non-scaling-stroke" />
              ))}
          </g>
        )}
        {adnotare && (
          /* `adnotareInCadru`: raportul validatorului poartă geometrie în
             coordonatele DOCUMENTULUI, iar ea nu trece prin traducerile din
             `canonical.ts`. Fără orientare și origine s-ar desena la sute de
             kilometri de planul pe care îl explică, sau în oglindă față de el. */
          <AnnotationLayer anno={adnotareInCadru(adnotare.anno, doc?.crs, origine)}
                           status={adnotare.status}
                           refRing={inelLocal(selectie, etajActiv)} />
        )}
        {/* Filmul împachetării, peste planșă: e o ÎNTREBARE despre desen, nu
            o parte din el. */}
        {(!vedeSituatia || cadruFilmInCadru?.situatie) && <FilmLayer cadru={cadruFilmInCadru} />}
        {/* RIGLA, ultimul strat: se desenează peste tot, fiindcă e unealta cu
            care te uiți la desen, nu o parte din el. */}
        {riglaDisponibila && <RiglaLayer masuri={masuri} inCurs={inCurs} pxPerM={view.k}
                                 viewTurn={rot} />}
      </g>
    </svg>
  );

  // --- Planul nivelului, pe masa editorului ----------------------------------
  //
  // Vederea «Plan» a unei soluții ARHITECTURALE gata, pe UN corp, se lucrează
  // pe aceeași masă ca un tronson (Stefan, 23.09.2026: «aplică interfața din
  // tronsoane … să funcționeze în același mod»). Masa ia locul pânzei; «2D» și
  // «3D» rămân desenul și macheta de până acum.
  //
  // DOCUMENTUL ÎNTREG, nu felia de start: o schimbare se duce și pe nivelurile
  // identice, deci ele trebuie să fie în document când se deschide masa.
  // PARCAJUL rămâne pe desenul lui: locurile, aleile și rampele nu sunt pe masă.
  const pePlanEditabil = editabil && plin && !compact && solution.discipline === "architecture"
    && solution.status === "ready" && !vedeSituatia && vedere === "plan";
  //
  // PE «TOATE CORPURILE» TOT PE MASĂ (Stefan, 24.09.2026: «să meargă și când
  // sunt toate corpurile, dar fiecare corp cu axurile proprii»): masa primește
  // toate planșeele cotei și ține unul o dată, cu celelalte desenate în jur
  // (v. `EditorNivel`). Numele rămâne la singular: e NIVELUL de editat, cu
  // toate corpurile lui.
  //
  // ȘI NUMAI LA CERERE (Stefan, 24.09.2026: «pune un buton care să activeze
  // modul de editare, nu intra automat»): planul se deschide ca desen, iar
  // masa vine la «Editează planul» și pleacă la «Închide editarea».
  const poateEdita = pePlanEditabil && potScrie && planseeActive.length > 0
    && planseeActive.every((n) => !n.parcare) && !!docLocal && felia.complet;
  const nivelDeEditat = editez && poateEdita ? planseeActive : null;
  const butonEditare = poateEdita && !nivelDeEditat ? (
    <button className="btn meniu-editeaza" title={t("viz.editeaza_planul_titlu")}
            onClick={() => { setSelectie(null); setEditez(true); }}>
      {t("viz.editeaza_planul")}
    </button>
  ) : poateEditaParcaj && !edParcaj.activ ? (
    // Pe subsol, același loc din meniu, alt editor: pe desen, nu pe masă.
    <button className="btn meniu-editeaza" title={t("viz.editeaza_parcajul_titlu")}
            onClick={() => { setSelectie(null); setEditezParcaj(true); }}>
      {t("viz.editeaza_parcajul")}
    </button>
  ) : null;
  // Recalcularea cere documentul SALVAT: cu modificări nesalvate în editor, ea
  // ar lucra pe planșa de dinainte, iar recitirea de după le-ar arunca.
  const areEvacuare = !!((doc?.diagnostics?.evacuare as { niveluri?: unknown[] } | undefined)
    ?.niveluri?.length);
  const recalculeaza = async () => {
    setEvacuareInLucru(true); setEvacuareRaport(null);
    try {
      const r = await api.recalculeazaEvacuarea(projectId, solution.id);
      // TOATE nivelurile evacuate: acțiunea stă acum în «⋯» al soluției, nu pe
      // nivelul de parcaj, deci se poate cere de pe orice cotă.
      const n = r.niveluri;
      // Un nivel care a picat, sau o soluție fără căi de evacuare, se spune:
      // «0 ieșiri» peste traseele vechi arăta ca o reușită (VM, 22.10.2026).
      const picat = n.find((x) => x.status === "a picat");
      if (picat || !n.length) {
        setEvacuareRaport({ rau: true, text: picat
          ? t("viz.evacuare_recalc_picat", { eroare: picat.eroare ?? "?" })
          : t("viz.evacuare_recalc_fara_nivel") });
        return;
      }
      const fara = n.reduce((a, x) => a + (x.fara_ruta ?? 0), 0);
      const negasite = n.flatMap((x) => x.iesiri_negasite ?? []);
      const lung = Math.max(...n.map((x) => x.cel_mai_lung_m ?? 0), 0);
      setEvacuareRaport({
        rau: fara > 0 || negasite.length > 0,
        text: fara || negasite.length
          ? [t("viz.evacuare_recalc_gata"),
             fara ? t("viz.evacuare_recalc_fara", { n: fara }) : "",
             negasite.length ? t("viz.evacuare_recalc_negasite", { iesiri: negasite.join(", ") }) : ""]
              .filter(Boolean).join(" ")
          : t("viz.evacuare_recalc_ok", { iesiri: n.reduce((a, x) => a + (x.iesiri ?? 0), 0),
                                          lung: lung.toFixed(1) }),
      });
      setReciteste((k) => k + 1);
    } catch (e) {
      setEvacuareRaport({ rau: true, text: String((e as Error)?.message ?? e) });
    } finally {
      setEvacuareInLucru(false);
    }
  };
  // În «⋯» al soluției, lângă «Refă excavația» (Oana, 22.10.2026); aici, în
  // meniul din stânga, rămâne doar ce se întâmplă: lucrul și raportul.
  const poateRecalculaEvacuarea = editabil && plin && !compact && potScrie
    && solution.discipline === "architecture" && solution.status === "ready" && areEvacuare;
  const meniuArhitectura = poateRecalculaEvacuarea
    ? (inchide: () => void) => (
      <button type="button" role="menuitem" className="meniu-rand"
              disabled={evacuareInLucru || edParcaj.nesalvat}
              title={edParcaj.nesalvat ? t("viz.evacuare_recalc_nesalvat")
                     : t("viz.evacuare_recalc_titlu")}
              onClick={() => { inchide(); void recalculeaza(); }}>
        {t("viz.evacuare_recalc")}
      </button>
    ) : undefined;
  // blocul soluției primește acțiunile vizualizatorului și dacă planșa e un subsol
  // (comutatorul variantelor căilor de evacuare apare numai acolo)
  const laSubsol = !vedeSituatia && nivelActiv != null && nivelActiv < 0;
  const blocCuMeniu = isValidElement<{ meniuArhitectura?: unknown; laSubsol?: boolean }>(blocSolutie)
    ? cloneElement(blocSolutie, { ...(meniuArhitectura ? { meniuArhitectura } : {}), laSubsol }) : blocSolutie;
  const butonEvacuare = (evacuareInLucru || evacuareRaport) && (
    evacuareInLucru
      ? <p className="note pg-recomp-lucru" style={{ margin: "2px 0" }}>
          <span className="pg-recomp-spin" aria-hidden="true" />{t("viz.evacuare_recalc_lucru")}</p>
      : <p className={`note${evacuareRaport!.rau ? " bad" : ""}`} style={{ margin: "2px 0" }}>
          {evacuareRaport!.text}</p>
  );

  // --- Previzualizare: numai pânza -------------------------------------------
  //
  // Aceleași `PlanView`/`ApartmentView`, aceeași încărcare de document, același
  // zoom — doar fără nimic în jur. Un al doilea desenator „mic" ar fi al doilea
  // loc în care un element își schimbă aspectul, iar cele două ar diverge exact
  // când unul e folosit ca dovadă pentru celălalt.
  if (compact) {
    return (
      <div className="panza-compact" ref={gazda}>
        {eroare && <p className="note bad">desenul nu s-a putut citi</p>}
        {!doc && !eroare && <p className="note">{t("viz.se_incarca")}</p>}
        {panza}
      </div>
    );
  }

  // --- Studio: canvasul pe tot, panourile peste el ---------------------------
  if (plin) {
    return (
      <div className="panza-tot" ref={gazda}>
        {eroare && <p className="note bad float" style={{ left: 16, top: 16 }}>{eroare}</p>}
        {/* SCHELETUL, nu un rând de text (Stefan, 19.09.2026: același
            placeholder ca la structură, pe toate trei disciplinele). Locul
            desenului îl ține clădirea însăși — amprenta, apoi nivelurile, la
            înălțimile lor reale — deci ecranul spune CE se încarcă, nu doar că
            se încarcă. Silueta vine cu lista de soluții, deci nu costă o
            cerere în plus; fără ea scheletul rămâne o clădire generică. */}
        {!doc && !eroare && (
          <div style={{ position: "absolute", inset: 0 }}>
            <ScheletIncarcare silueta={silueta} />
          </div>
        )}
        {/* Macheta ÎN LOCUL pânzei, nu peste ea: e altă reprezentare a
            aceluiași nivel, nu un strat. Pânza rămâne montată dedesubt —
            demontată, s-ar pierde încadrarea și rotirea, iar întoarcerea la
            «Randat» ar reîncadra desenul de la zero. */}
        <div style={{ position: "absolute", inset: 0,
                      visibility: macheta || (vedeSituatia && machetaSit)
                        || nivelDeEditat ? "hidden" : "visible" }}>
          {panza}
        </div>
        {nivelDeEditat && docLocal && (
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, top: 0 }}>
            <Suspense fallback={<p className="note float" style={{ left: 16, top: 16 }}>
                                  {t("viz.se_incarca")}</p>}>
              <EditorNivel key={nivelDeEditat.map((n) => n.id).join(",")}
                           projectId={projectId} solutionId={solution.id}
                           doc={docLocal} origine={origine}
                           storeyIds={nivelDeEditat.map((n) => n.id)} potScrie={potScrie}
                           sus={<>{blocCuMeniu}{selectorVederi}</>}
                           jos={cifre || undefined}
                           colt={selectorMod || undefined}
                           laInchide={() => { if (potPleca()) setEditez(false); }}
                           laSalvat={() => setReciteste((n) => n + 1)} />
            </Suspense>
          </div>
        )}
        {macheta && scena3 && (
          <Suspense fallback={
            <p className="note float" style={{ left: 16, top: 16 }}>
              Se încarcă macheta…
            </p>}>
            <Randare3D scena={scena3} vedere={macheta}
                       corpAles={corpActiv === TOATE_CORPURILE ? null
                                 : (arhActiv?.cod ?? null)}
                       ales={eidSelectat} laAles={alegeDinMacheta} />
          </Suspense>
        )}

        {/* Macheta amplasamentului. Stă ÎN LOCUL pânzei, ca și cea de nivel, iar
            pânza rămâne montată dedesubt cu încadrarea ei — întoarcerea la
            «Plan» nu reîncadrează desenul de la zero. */}
        {vedeSituatia && machetaSit && scenaSituatie && (
          <Suspense fallback={
            <p className="note float" style={{ left: 16, top: 16 }}>
              Se încarcă macheta…
            </p>}>
            <RandareSit3D scena={scenaSituatie} vedere={vedereaSit}
                          soare={{ alt: soarele.alt, az: soarele.az }}
                          selectie={selMacheta.elemente} zone={selMacheta.zone}
                          laAlegere={alegePeMacheta} laZona={zonaPeMacheta}
                          coloane={coloaneMacheta} aprins={aprinsMacheta}
                          cote={coteMacheta ? eidActiv : null}
                          coteValori={coteValoriMacheta}
                          etaj={etajAles} planuri={niveluriArh ?? undefined}
                          oras={stratOras} />
          </Suspense>
        )}
        {/* Sursa hărții, cerută de licența OSM, oriunde se vede orașul: pe
            plan și pe machetă. */}
        {orasul.oras && (
          <span className="oras-atribuire">{orasul.oras.atribuire}</span>
        )}

        {/* DETALIILE ELEMENTULUI ALES: fereastra apare numai cât e ales ceva. */}
        {vedeSituatia && machetaSit && eidActiv && (
          <DetaliiElement projectId={projectId}
                          doc={docArh}
                          elemente={selMacheta.elemente} activ={eidActiv} laActiv={setActivMacheta}
                          laInchide={() => setSelMacheta({ elemente: [], zone: [] })}
                          cote={coteMacheta} laCote={setCoteMacheta} />
        )}

        {/* Bara vederilor: un rând, sub taburile de disciplină, cu grupurile
            etichetate — corpul, cota, felul desenului — și cifrele nivelului la
            capătul din dreapta. Straturile rămân DEDESUBT, într-o coloană:
            sunt paisprezece bife, adică un panou, nu un grup de pastile.

            Coloana ține amândouă fiindcă înălțimea barei nu se poate scrie de
            mână: cu douăsprezece cote și patru corpuri ea se rupe pe două
            rânduri, iar straturile așezate la o cotă fixă i-ar cădea peste. */}
        {/* UN SINGUR MENIU PLUTITOR (Corecția lui Stefan, 25.09): sus
            soluția — numele, fazele, acțiunile, «Schimbă ▾» —, dedesubt
            grupurile vederii. Deasupra planșei nu mai plutește nimic altceva,
            deci meniul urcă sub antet. */}
        {!nivelDeEditat && (blocSolutie || selectorVederi
          || selectorAfisareSit || selectorCamera
          || selectorTus || selectorDepanare) && (
          <div className="float coloana-stanga" style={{ left: 16, top: 16 }}>
            {(blocSolutie || selectorVederi
              || selectorAfisareSit || selectorCamera
              || selectorTus || selectorDepanare) && (
              <div className="card bara-vederi meniu-stanga">
                {blocCuMeniu}
                {butonEditare}
                {butonEvacuare}
                {selectorVederi}
                {selectorCamera}
                {selectorEtaj}
                {selectorAfisareSit}
                {selectorTus}
                {selectorFiltre}
                {selectorDepanare}
                {cifre}
                {cifreGenerale}
              </div>
            )}
          </div>
        )}

        {cutieLegenda && !macheta && !(vedeSituatia && machetaSit) && !nivelDeEditat && (
          <div className="float" style={{ left: 16, bottom: 16 }}>{cutieLegenda}</div>
        )}
        {/* Bara editorului de parcaj, sus, la mijloc: meniul din stânga și busola
            din dreapta rămân la locul lor. */}
        {edParcaj.activ && (
          <div className="float" style={{ left: "50%", top: 16, transform: "translateX(-50%)" }}>
            {edParcaj.bara}
          </div>
        )}

        {/* Rigla se centrează ÎN SPAȚIUL RĂMAS, nu pe ecran. Centrată pe ecran,
            intra sub panoul de însorire din dreapta la orice fereastră sub vreo
            1.100 px — iar panoul e opac, deci capătul riglei pur și simplu
            dispărea. `right` o oprește înainte de el, `margin: auto` o ține
            centrată în ce rămâne. */}
        {baraSoare && (
          <div className="float bara-soare-loc" style={{ left: 16,
                                                         right: panouInsorire ? 334 : 250,
                                                         bottom: 16 }}>
            {baraSoare}
          </div>
        )}
        {/* PE SITUAȚIE, COLOANA DIN DREAPTA: panoul fațadei, sub el asistentul,
            apoi panoul de însorire și, jos, comutatorul 2D/3D cu butonul
            asistentului lângă el. O singură coloană, cu aceeași margine ca
            panoul din stânga: nimic nu se suprapune și nimic nu trece de antet.
            Locul butonului rămâne rezervat și pe desenul 2D, unde asistentul
            lipsește, ca 2D/3D să nu sară de sub cursor la trecerea în 3D. */}
        {vedeSituatia && !compact ? (
          <div className="float coloana-dreapta">
            {/* BUSOLA, PRIMA ÎN COLOANĂ, nu plutind peste ea: plutea la aceleași coordonate ca
                panourile coloanei și le acoperea colțul (Oana, 30.10.2026) */}
            {busola && !machetaSit && !nivelDeEditat && (
              <div className="busola-coloana">{busola}</div>
            )}
            {/* ASISTENTUL PLANULUI DE SITUAȚIE, numai pe o soluție de plan: discuția
                în același loc din coloană ca a fațadei, bula în colțul din
                dreapta-jos; fără panou (Oana, 31.10.2026: cifrele sunt în
                «Indicatori», «Refă planul cu agentul» în meniul soluției). După
                un lot scris, documentul se recitește moale, pe loc. */}
            {solution.discipline === "site_plan" && (
              <AsistentPlan projectId={projectId} planId={solution.id} summary={solution.summary}
                            laSchimbare={() => setReciteste((x) => x + 1)}
                            loc={locAsistentPlan} />
            )}
            {machetaSit && (
              // PANOUL DE FAȚADĂ E MONTAT MEREU pe machetă, iar comutatorul doar
              // îl arată: asistentul și firul agentului sunt ale lui, deci un
              // panou demontat lua cu el și butonul. Ascuns, își păstrează și
              // regulile nesalvate, și discuția.
              <div className="coloana-fatada" style={araFatada ? undefined : { display: "none" }}>
                <PanouFatada projectId={projectId}
                             arhitecturaId={solution.discipline === "architecture" ? solution.id : arhId}
                             planSituatieId={solution.discipline === "site_plan" ? solution.id
                                             : (solution.lineage?.site_plan ?? null)}
                             doc={solution.discipline === "architecture" ? (docLocal ?? null) : docArh}
                             laPreview={(ob) => {
                               if (solution.discipline === "architecture") puneFatadaProprie(ob);
                               else puneFatada(ob);
                             }}
                             selectie={selMacheta}
                             laSelectie={setSelMacheta}
                             laAprinde={setAprinsMacheta}
                             laSchimbare={(adoptat) => {
                               // pe o arhitectură, recitirea editorului (pânza rămâne);
                               // pe situație, arhitectura ei se reîntreabă — cheia
                               // (`updated_at`, revizia fațadei) hotărăște dacă se aduce.
                               // `adoptat`: documentul scris e deja pe ecran (instantaneu)
                               if (solution.discipline === "architecture") {
                                 if (!adoptat) setReciteste((x) => x + 1);
                               } else {
                                 if (adoptat) adoptaCheiaArh.current = true;
                                 arhAdusa.current = null; setReincearcaArh((x) => x + 1);
                               }
                             }}
                             macheta={machetaFatadei}
                             locAsistent={locAsistent} />
              </div>
            )}
            <div ref={setLocPanouAs} className="loc-asistent" />
            {panouInsorire}
            <div className="colt-rand">
              {comutatorVedere && !nivelDeEditat && (
                <div className="card colt-vedere">{comutatorVedere}</div>
              )}
              {peSituatie && fatadaLaVedere && (
                <div ref={setLocButonPlan} className="loc-buton-asistent" />
              )}
              <div ref={setLocButonAs} className="loc-buton-asistent" />
            </div>
          </div>
        ) : (panouInsorire || (comutatorVedere && !nivelDeEditat)) && (
          // Colțul din dreapta-jos: panoul de însorire, când e deschis, și SUB
          // el comutatorul vederii — o coloană, ca să nu se calce.
          <div className="float colt-dreapta-jos">
            {panouInsorire}
            {comutatorVedere && !nivelDeEditat && (
              <div className="card colt-vedere">{comutatorVedere}</div>
            )}
          </div>
        )}
        {/* Colțul din DREAPTA-SUS, într-o singură coloană: busola peste cheia
            de legendă. Se citesc o dată, la început, împreună cu desenul — nu
            după ce ochiul a coborât în colțul de jos. Cheia se dă la o parte
            când se deschide inspectorul, care ocupă același colț și răspunde
            la o întrebare mai precisă.

            STIVUITE, nu suprapuse: până când busola a apărut și pe planurile
            de nivel, cele două nu se puteau vedea niciodată deodată — una
            cerea planul de situație, cealaltă îl excludea — și stăteau amândouă
            la aceleași coordonate. */}
        {busola && !(vedeSituatia && machetaSit) && !(vedeSituatia && !compact) && !nivelDeEditat && (
          <div className="float" style={{ right: 16, top: 56, display: "flex",
                                          flexDirection: "column",
                                          alignItems: "flex-end", gap: 8 }}>
            {busola}
          </div>
        )}

        {descris && !nivelDeEditat && (
          <div className="float card inspector-plutitor">
            <Inspector info={descris} onClose={() => setSelectie(null)}
                       onSelect={setSelectie} extra={panouRecomp} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card plansa">
      <div className="plansa-cap">
        <b>{solution.label}</b>
        <span className="lbl">
          document canonic · {doc?.objects?.length ?? "—"} obiecte
        </span>
        <div className="sp" />
        {selectorCompact}
      </div>

      {eroare && <p className="note bad">{eroare}</p>}
      {!doc && !eroare && <p className="note">{t("viz.se_incarca_desenul")}</p>}
      {doc && !cadru && <p className="note">Documentul nu are geometrie de desenat.</p>}

      {cadru && (
        <div className="vizualizator">
          <div className="panza-cutie" ref={gazda}>
            {panza}
            {cutieLegenda && (
              <div className="float" style={{ left: 12, bottom: 12 }}>{cutieLegenda}</div>
            )}
            {busola && (
              <div className="float" style={{ right: 12, top: 12 }}>{busola}</div>
            )}
          </div>

          <aside className="vizualizator-inspector">
            {descris
              ? <Inspector info={descris} onClose={() => setSelectie(null)}
                           onSelect={setSelectie} extra={panouRecomp} />
              : <p className="note">
                  Click pe un loc de parcare, un apartament sau o cameră ca să
                  vezi ce e.
                </p>}
          </aside>
        </div>
      )}

    </div>
  );
}
